/**
 * Per-page chrome: heroes, eyebrows, stat-strip labels, empty states, filter
 * names and category labels.
 *
 * Split from `common` because these strings are *about a page* rather than
 * about the product. Keeping them together means a new page's hero is one
 * import away rather than a hunt through a 2,000-line file.
 *
 * Only chrome lives here. Topic titles, group names, chat messages and
 * captions are user or generated content and stay in whatever language they
 * were created in — translating a learner's own topic title would be editing
 * their words, not localising the app. Category names are the one boundary
 * case: they are a fixed enum stored in English and drive filtering, so only
 * the label is translated and the stored value stays the English slug.
 */

import type { PartialTranslation } from "./index";

export const pages: PartialTranslation["pages"] = {
  topics: {
    title: "Khám phá chủ đề",
    description: "Tìm một chủ đề đáng nói, rồi luyện tập nói to.",
    eyebrow: "Khám phá",
    metaLabel: "Luyện hội thoại",
    metaDescription: "Luyện kỹ nói hội thoại với các câu hỏi thảo luận do AI tạo ra và phản hồi theo thời gian thực.",
    communityLabel: "Chủ đề cộng đồng",
    communityDescription: "Xem các chủ đề luyện tập do người học và giảng viên khác tạo và chia sẻ.",
    aiLabel: "Dựa trên AI",
    aiDescription: "Tạo chủ đề luyện tập mới phù hợp với trình độ, sở thích và mục tiêu học tập của bạn.",
    activeLearners: "Người học đang hoạt động",
    practisingNow: "đang luyện tập",
    createdByYou: "do bạn tạo",
    statsLabel: "Thống kê thư viện chủ đề",
    libraryTotal: "Chủ đề trong thư viện",
    categoryCount_other: "{{count}} chuyên mục",
    myTopics: "Chủ đề của tôi",
    pickUpTitle: "Tiếp tục từ chỗ bạn đang dở",
    categoriesLabel: "Chuyên mục",
    allCategories: "Tất cả",
    searchPlaceholder: "Tìm chủ đề...",
    createTopic: "Tạo chủ đề",
    clearSearch: "Xóa tìm kiếm",
    noQuestionsYet: "Chưa có câu hỏi nào",
    filterAllTitle: "Tất cả chủ đề",
    emptyAllTitle: "Chưa có chủ đề nào",
    emptyAllDescription: "Hãy là người đầu tiên tạo một chủ đề công khai!",
    categoryTitle: "Chuyên mục: {{name}}",
    noTopicsInCategory: "Chưa có chủ đề trong {{name}}",
    noTopicsInCategoryDescription: "Hãy quay lại sau để xem chủ đề mới trong chuyên mục này.",
    showingPage: "Trang {{page}} / {{totalPages}} (tổng {{total}} chủ đề)",
    resultCount: "{{count}} chủ đề",
    practiseNow: "Luyện ngay",
    resultsHeading: "Kết quả chủ đề",
    sortAria: "Sắp xếp chủ đề",
    practise: "Luyện tập",
    viewTopic: "Xem",
    filterAria: "Lọc chủ đề",
    loadErrorTitle: "Không tải được chủ đề",
    loadErrorDescription:
      "Thư viện không phản hồi. Thường là do kết nối mạng chứ không phải thư viện của bạn — không mất dữ liệu nào.",
    loadErrorRetry: "Thử lại",
    noMatchTitle: "Không có chủ đề nào khớp “{{query}}”",
    noMatchDescription: "Hãy thử từ khác, hoặc xoá tìm kiếm để xem toàn bộ thư viện.",
    emptyCategoryTitle: "Chưa có gì trong {{category}}",
    emptyCategoryDescription: "Chưa ai đăng bài ở đây. Phần còn lại của thư viện vẫn đang chờ bạn.",
    browseAllTopics: "Xem tất cả chủ đề",
    seoAllDescription: "Khám phá các chủ đề luyện nói công khai do cộng đồng tạo. Luyện tập với câu hỏi và từ vựng để cải thiện kỹ năng hội thoại.",
    seoCategoryDescription: "Khám phá các chủ đề luyện nói trong chuyên mục {{name}}. Luyện tập với câu hỏi và từ vựng để cải thiện kỹ năng hội thoại.",
    seoFilterDescription: "Xem {{name}} để luyện nói. Cải thiện kỹ năng hội thoại với các chủ đề chọn lọc.",
    categories: {
      General: "Chung",
      Business: "Kinh doanh",
      Travel: "Du lịch",
      Technology: "Công nghệ",
      "Health & Fitness": "Sức khỏe & Thể hình",
      Education: "Giáo dục",
      Entertainment: "Giải trí",
      "Daily Life": "Cuộc sống hằng ngày",
      Lifestyle: "Phong cách sống",
      Social: "Xã hội",
      Politics: "Chính trị",
      History: "Lịch sử",
      Sports: "Thể thao",
      "Food & Cooking": "Ẩm thực",
      Finance: "Tài chính",
      "Art & Design": "Nghệ thuật & Thiết kế",
      Environment: "Môi trường",
    },
    showingTopics_other: "Đang hiển thị {{count}} chủ đề",
    filterRecentPracticeTitle: "Luyện tập gần đây",
    filterRecentPracticeEmptyTitle: "Chưa luyện tập gì",
    filterRecentPracticeEmptyDescription: "Hãy luyện tập các chủ đề của bạn để thấy hoạt động gần đây tại đây.",
    filterRecentStudiedTitle: "Đã học gần đây",
    filterRecentStudiedEmptyTitle: "Chưa có lịch sử học",
    filterRecentStudiedEmptyDescription: "Hãy bắt đầu học các chủ đề để theo dõi tiến độ học tập của bạn.",
    filterBookmarkedTitle: "Chủ đề đã lưu",
    filterBookmarkedEmptyTitle: "Chưa lưu chủ đề nào",
    filterBookmarkedEmptyDescription: "Hãy lưu các chủ đề để dễ tìm lại sau này.",
    filterPinnedTitle: "Chủ đề đã ghim",
    filterPinnedEmptyTitle: "Chưa ghim chủ đề nào",
    filterPinnedEmptyDescription: "Hãy ghim những chủ đề yêu thích để truy cập nhanh.",
    filterRecentTitle: "Chủ đề gần đây",
    filterRecentEmptyTitle: "Chưa có chủ đề nào",
    filterRecentEmptyDescription: "Tạo chủ đề đầu tiên để bắt đầu luyện tập.",
    filterTrendingTitle: "Chủ đề thịnh hành",
    filterTrendingEmptyTitle: "Chưa có chủ đề thịnh hành",
    filterTrendingEmptyDescription: "Hãy là người đầu tiên tạo chủ đề thịnh hành!",
    filterPopularTitle: "Phổ biến nhất",
    filterPopularEmptyTitle: "Chưa có chủ đề phổ biến nào",
    filterPopularEmptyDescription: "Hãy thích các chủ đề để chúng trở nên phổ biến hơn!",
    filterMostBookmarkedTitle: "Được lưu nhiều nhất",
    filterMostBookmarkedEmptyTitle: "Chưa có chủ đề được lưu nào",
    filterMostBookmarkedEmptyDescription: "Hãy lưu các chủ đề để xem lại sau này!"
  },
  /**
   * `/vocabulary/practice` — bàn học: tiêu đề của nguồn luyện tập, các tab chế
   * độ, thanh đánh giá dùng chung và toàn bộ lớp vỏ của câu hỏi. Nội dung học
   * tập (từ, định nghĩa, ví dụ, phiên âm, loại từ, cấp độ CEFR) là dữ liệu do
   * người dùng hoặc AI tạo ra, nên cố ý không đưa vào đây.
   */
  vocabularyPractice: {
    seoTitle: "Luyện tập từ vựng | Lyder Talk",
    seoDescription: "Luyện tập từ vựng đã lưu của bạn với thẻ ghi nhớ và bài tập viết",
    notFoundTitle: "Không tìm thấy từ vựng",
    noSelectionTitle: "Chưa chọn từ vựng nào",
    notFoundDescription: "Chưa có từ vựng nào được chọn để luyện tập.",
    goToVocabulary: "Đến trang Từ vựng",
    caughtUpTitle: "Bạn đã ôn xong!",
    caughtUpDescription: "Hôm nay không có từ vựng nào đến hạn ôn. Làm tốt lắm!",
    noVocabularySelected: "Chưa chọn từ vựng nào.",
    back: "Quay lại",
    backToVocabulary: "Về trang từ vựng",
    viewSession: "Xem buổi học: {{topic}}",
    viewCollection: "Xem bộ sưu tập: {{title}}",
    labelTopic: "Chủ đề:",
    labelCollection: "Bộ sưu tập:",
    savedOfTotal_other: "Đã lưu {{saved}} trong {{total}} từ vào từ vựng của bạn",
    modes: {
      due: "Ôn hằng ngày",
      selected: "Tự chọn",
      session: "Luyện theo buổi học",
      collection: "Ôn theo bộ sưu tập",
      flashcard: "Thẻ ghi nhớ",
      write: "Viết",
      listening: "Nghe",
      scramble: "Ghép chữ",
      pronunciation: "Phát âm",
      descriptionFlashcard: "Ôn các thẻ với phương pháp lặp lại ngắt quãng",
      descriptionWrite: "Gõ từ còn thiếu hoặc điền vào chỗ trống",
      descriptionListening: "Nghe từ đó rồi gõ lại những gì bạn nghe được",
      descriptionScramble: "Sắp xếp lại các chữ cái để tạo thành từ",
      descriptionPronunciation: "Nói từ đó to ra và kiểm tra phát âm",
      match: "Ghép cặp",
      descriptionMatch: "Đua với đồng hồ để ghép từ với nghĩa của nó",
    },
    toolbar: {
      sortByReviewDate: "Sắp xếp theo ngày ôn",
      shuffleOrder: "Xáo trộn thứ tự",
      stopAudio: "Tắt âm thanh",
      playAudio: "Phát âm thanh",
      shortcuts: "Phím tắt",
      keyboardShortcuts: "Phím tắt bàn phím",
      flipCard: "Lật thẻ",
      submit: "Gửi",
      hintSkip: "Gợi ý / Bỏ qua",
      review: "Đánh giá",
      shuffle: "Xáo trộn",
    },
    review: {
      again: "Chưa nhớ",
      hard: "Khó",
      good: "Tốt",
      easy: "Dễ",
      question: "Bạn nhớ từ này thế nào?",
      reviewed: "Đã ôn",
      reviewCount: "Đã ôn {{count}} lần",
    },
    skip: {
      aria: "Bỏ qua từ này (đã biết rồi)",
      title: "Bỏ qua (S)",
    },
    hint: {
      trigger: "Gợi ý",
      label: "Gợi ý: ",
    },
    match: {
      prompt: "Chọn một từ, rồi chọn nghĩa của nó",
      wordColumn: "Từ",
      meaningColumn: "Nghĩa",
      time: "Thời gian còn lại",
      lives: "Còn {{count}} lượt",
      pairsProgress: "{{done}}/{{total}} cặp",
      boardCleared: "Xong bàn!",
      boardClearedBody: "+{{seconds}} giây · thêm {{count}} cặp",
      deckClearedBody: "Bạn đã ghép hết số từ của buổi này.",
      nextBoard: "Bàn tiếp theo",
      seeResults: "Xem kết quả",
      outOfHearts: "Hết lượt",
      timeUp: "Hết giờ",
      sessionCleared: "Xong cả buổi",
      gameOverBody: "Ghép được {{matched}} từ · {{score}} điểm",
      bestCombo: "Chuỗi tốt nhất {{count}}×",
      playAgain: "Chơi lại",
      backToCards: "Về thẻ ghi nhớ",
      progressSaved: "Tiến độ được lưu ngay khi bạn ghép.",
    },
    complete: {
      allDone: "Bạn đã làm xong!",
      title: "Hoàn tất luyện tập",
      correct: "Đúng",
      incorrect: "Sai",
      skipped: "Đã bỏ qua",
      scoreCorrect: "đúng",
      perfect: "Xuất sắc",
      great: "Rất tốt",
      good: "Tốt",
      keepGoing: "Cố lên",
      bestStreak: "Chuỗi tốt nhất: {{count}}",
      resultsBreakdown: "Kết quả chi tiết ({{count}})",
      missedWords: "Từ làm sai ({{count}})",
    },
    sections: {
      definition: "Định nghĩa",
      example: "Ví dụ",
      words: "Từ",
    },
    empty: {
      description: "Bạn cần có ít nhất một vài từ vựng đã lưu để bắt đầu luyện tập.",
      goBack: "Quay lại",
    },
    answerLengthAria: "Độ dài câu trả lời",
    check: "Kiểm tra",
    fillInTheBlank: "Điền vào chỗ trống",
    typeTheWord: "Gõ từ đó",
    typeTheWordYouHeard: "Gõ từ bạn vừa nghe",
    enterWordPlaceholder: "Nhập từ...",
    typeMissingWordPlaceholder: "Gõ từ còn thiếu...",
    letters: "chữ cái",
    tapToReveal: "Chạm để xem",
    clickOrSpaceToReveal: "Nhấp chuột hoặc phím Space để xem",
    scramblePrompt: "Sắp xếp các chữ cái để tạo thành từ",
    scrambleAllPlaced: "Đã đặt hết chữ cái — hãy kiểm tra.",
    startsWithHint: 'Bắt đầu bằng "{{letter}}" ({{count}} chữ cái)',
    lettersHint: "{{hint}}... ({{count}} chữ cái)",
    listening: {
      carefully: "Hãy nghe thật kỹ",
      playTitle: "Phát (Space)",
      playAria: "Nghe phát âm",
      audioUnavailable: "Trình duyệt này không hỗ trợ âm thanh. Đây là gợi ý phát âm:",
      replay: "Phát lại",
      slowPlaybackTitle: "Bật/tắt phát chậm",
      slow: "Chậm",
      normal: "Bình thường",
    },
    sidebar: {
      hidden: "Đã ẩn",
      collapse: "Thu gọn",
      openList: "Mở danh sách từ",
    },
    toasts: {
      reviewed: "Đã ôn: {{difficulty}}",
      savedReviewed: "Đã lưu & ôn: {{difficulty}}",
      nextReview: '"{{word}}" — Ôn tiếp {{when}}',
    },
  },
  /** `/vocabulary/notes` + các thẻ ghi chú dùng chung (`WordNotes`, `VocabularyNoteCard`). */
  vocabularyNotes: {
    seoTitle: "Ghi chú | Lyder Talk",
    seoDescription: "Ghi chú từ vựng và ghi chú cá nhân của bạn, tất cả ở một nơi",
    heroTitle: "Ghi chú",
    heroDescription: "Ghi chú bạn đã ghi về từ vựng khi luyện tập, cùng ghi chú cá nhân của bạn — tất cả ở một nơi.",
    badgeWordNotes: "Ghi chú từ",
    badgeWordNotesDescription: "Ghi chú gắn với các từ vựng bạn đã học trong buổi luyện tập.",
    badgePersonalNotes: "Ghi chú cá nhân",
    badgePersonalNotesDescription: "Ghi chú tự do — ý tưởng, nhắc nhở, hoặc bất cứ điều gì bạn muốn nhớ.",
    metaPersonal: "Cá nhân",
    newNote: "Ghi chú mới",
    createFirstNote: "Tạo ghi chú đầu tiên",
    signInTitle: "Đăng nhập để xem ghi chú của bạn",
    signInDescription: "Ghi chú chỉ thuộc về tài khoản của bạn. Hãy đăng nhập để tạo và xem chúng.",
    emptyTitle: "Chưa có ghi chú nào",
    emptyDescription: "Hãy ghi chú các từ vựng khi luyện tập, hoặc tự viết ghi chú cá nhân tại đây.",
    noMatchTitle: "Không có ghi chú nào khớp tìm kiếm",
    noMatchDescription: "Hãy thử một từ khoá khác hoặc bỏ bớt bộ lọc.",
    searchPlaceholder: "Tìm ghi chú...",
    filterAria: "Lọc ghi chú",
    filterWordNotes: "Ghi chú từ",
    filterPersonal: "Cá nhân",
    /** `WordNotes` — dùng chung cho trang ghi chú, tab từ điển và buổi luyện tập. */
    myNotes: "Ghi chú của tôi",
    noteCount_one: "{{count}} ghi chú",
    noteCount_other: "{{count}} ghi chú",
    unknownWord: "Từ không xác định",
    addNote: "Thêm ghi chú",
    wordNotesTitle: "Ghi chú từ vựng",
    wordNotesHint: "Tra cứu một từ để xem các ghi chú bạn đã ghi về từ đó khi luyện tập",
    addNoteForWord: 'Thêm ghi chú hoặc mẹo nhớ cho "{{word}}".',
    practiceEmptyHint: "Ghi chú được thêm từ biểu tượng ghi chú trên một từ vựng trong lúc luyện tập.",
  },
  /** Trang danh sách `/vocabulary/collections` cùng hero / thống kê / thanh công cụ / thẻ. */
  vocabularyCollections: {
    seoTitle: "Bộ sưu tập từ vựng | Lyder Talk",
    seoDescription: "Khám phá các bộ từ vựng dùng chung do cộng đồng tạo",
    heroTitle: "Bộ sưu tập từ vựng",
    heroDescription: "Khám phá và nhập các bộ từ dùng chung từ cộng đồng. Xây bộ từ chọn lọc nhanh hơn nhờ tận dụng công sức của người học khác.",
    createCollection: "Tạo bộ sưu tập",
    browseTopics: "Khám phá chủ đề",
    badgeCuratedSets: "Bộ chọn lọc",
    badgeCuratedSetsDescription: "Các bộ từ vựng được chọn tay về chủ đề từ du lịch đến kinh doanh, sẵn sàng nhập chỉ bằng một cú chạm.",
    badgeCommunityLibrary: "Thư viện cộng đồng",
    badgeCommunityLibraryDescription: "Khám phá những gì người học khác đang học. Theo dõi tác giả, nhập bộ của họ và cá nhân hoá.",
    badgeImport: "Nhập & cá nhân hoá",
    badgeImportDescription: "Mang bất kỳ bộ công khai nào vào, rồi chỉnh sửa, bổ sung và lồng vào lịch ôn ngắt quãng của bạn.",
    libraryLabel: "Thư viện",
    /** `{{number}}` đã được định dạng theo ngôn ngữ ứng dụng; `count` chỉ chọn số nhiều. */
    librarySets_one: "{{number}} bộ",
    librarySets_other: "{{number}} bộ",
    libraryWords_one: "{{number}} từ",
    libraryWords_other: "{{number}} từ",
    statsAria: "Thống kê bộ sưu tập",
    statsCommunitySets: "Bộ của cộng đồng",
    statsWordsToImport: "{{number}} từ để nhập",
    statsMyCollections: "Bộ của tôi",
    statsCreatedByYou: "bạn tự tạo",
    statsInProgress: "Đang học",
    statsSavingWords: "đang lưu từ",
    statsFromTopics: "Từ chủ đề",
    statsAutoGenerated: "tự động tạo",
    toolbar: {
      searchPlaceholder: "Tìm bộ sưu tập...",
      sortMostWords: "Nhiều từ nhất",
      filterMine: "Của tôi",
      filterTopics: "Từ chủ đề",
      filterPublic: "Công khai",
      filterShared: "Chia sẻ với tôi",
      advancedFilters: "Bộ lọc nâng cao",
      refineByCreator: "Lọc theo tác giả",
      creator: "Tác giả",
      allCreators: "Tất cả tác giả",
      clearCreatorFilter: "Bỏ lọc theo tác giả",
      filterOwnershipAria: "Lọc theo nguồn sở hữu",
      showingOf_one: "Đang hiện {{filtered}} trên {{total}} bộ sưu tập",
      showingOf_other: "Đang hiện {{filtered}} trên {{total}} bộ sưu tập",
      creatorChip: "Tác giả: {{name}}",
      clearChip: "Bỏ {{label}}",
    },
    empty: {
      noMatchTitle: "Không có bộ sưu tập phù hợp",
      myTitle: "Chưa có bộ sưu tập nào",
      communityTitle: "Chưa có bộ sưu tập công khai nào",
      noMatchDescription: "Hãy thử một từ khoá khác hoặc bỏ bớt bộ lọc.",
      myDescription: "Tạo bộ sưu tập từ vựng đầu tiên của bạn và chia sẻ với mọi người.",
      communityDescription: "Hãy là người đầu tiên tạo một bộ từ vựng dùng chung.",
      clearFilters: "Xoá bộ lọc",
      step: "Bước {{count}}",
      steps: {
        browse: "Khám phá",
        browseDescription: "Khám phá các bộ từ công khai do cộng đồng tạo",
        import: "Nhập",
        importDescription: "Nhập bất kỳ bộ nào vào bộ từ của bạn chỉ bằng một cú chạm",
        practice: "Luyện tập",
        practiceDescription: "Lặp lại ngắt quãng giúp từ vựng ghi lâu vào trí nhớ",
        pickTopic: "Chọn chủ đề",
        pickTopicDescription: "Quyết định bạn muốn chọn lọc từ vựng nào",
        addWords: "Thêm từ",
        addWordsDescription: "Xây bộ sưu tập của bạn với định nghĩa và ví dụ",
      },
    },
    card: {
      wordCount_one: "{{number}} từ",
      wordCount_other: "{{number}} từ",
      auto: "Tự động",
      complete: "Hoàn thành",
      fromTopic: "Từ chủ đề",
      yourProgress: "Tiến trình của bạn",
      progressAria: "Tiến trình bộ sưu tập",
      anonymous: "Ẩn danh",
      public: "Công khai",
      private: "Riêng tư",
    },
    /** `/vocabulary/collections/:id` — hero, dải thống kê, thanh công cụ, hàng từ, hộp thoại chia sẻ. */
    detail: {
      seoTitle: "Bộ sưu tập từ vựng | Lyder Talk",
      notFoundTitle: "Không tìm thấy bộ sưu tập",
      notFoundDescription: "Bộ từ vựng này không tồn tại hoặc đã bị xoá.",
      browseCollections: "Khám phá bộ sưu tập",
      /** Hero (`CollectionDetailHero`). */
      backToCollections: "Quay lại bộ sưu tập",
      descriptionFallback: "Một bộ từ vựng dùng chung.",
      badgePublicDescription: "Bất kỳ ai cũng có thể tìm thấy và nhập bộ sưu tập này.",
      badgePrivateDescription: "Chỉ bạn mới có thể truy cập bộ sưu tập này.",
      badgeWordCountDescription: "Tổng số mục từ vựng trong bộ sưu tập này.",
      addWords: "Thêm từ",
      removeImport: "Bỏ nhập",
      byAuthor: "bởi {{name}}",
      reviewedOfTotal: "Đã ôn {{reviewed}} trên {{total}} từ",
      collectionLabel: "Bộ sưu tập",
      hintAddWords: "Thêm từ để bắt đầu luyện tập",
      hintSaved: "Đã lưu vào thư viện của bạn",
      hintPractice: "Luyện tập để bắt đầu ôn tập",
      shareable: "Có thể chia sẻ",
      /** Dải thống kê (`CollectionDetailStats`). */
      statsAria: "Thống kê bộ sưu tập",
      masteryAria: "Mức độ thành thạo của bộ sưu tập",
      wordsInSet: "Số từ trong bộ",
      leftToReview: "Còn {{number}} từ để ôn",
      inYourLibrary: "Trong thư viện của bạn",
      percentSaved: "Đã lưu {{percent}}%",
      reviewedCount: "Đã ôn {{number}} từ",
      savedCount: "Đã lưu {{number}} từ",
      /** Thanh công cụ (`CollectionDetailToolbar`). */
      refineByLevelOrType: "Lọc theo trình độ hoặc loại từ",
      noFilterableAttributes: "Bộ sưu tập này không có thuộc tính nào để lọc.",
      filterUnreviewed: "Chưa ôn",
      showingOf_one: "Đang hiện {{filtered}} trên {{total}} từ",
      showingOf_other: "Đang hiện {{filtered}} trên {{total}} từ",
      /** Hàng từ (`CollectionDetailWordRow`). */
      dragToReorder: "Kéo để sắp xếp lại",
      moreDetails: "Thêm chi tiết",
      showDetails: "Xem chi tiết",
      hideDetails: "Ẩn chi tiết",
      /** Trạng thái rỗng (`CollectionDetailEmptyState`). */
      emptyTitle: "Bộ sưu tập này còn trống",
      emptyOwnerDescription:
        "Thêm những từ đầu tiên để bắt đầu xây dựng bộ sưu tập này. Bạn có thể tìm trong thư viện hoặc để AI gợi ý.",
      emptyOtherDescription: "Chủ sở hữu chưa thêm từ nào vào bộ sưu tập này. Hãy quay lại sau.",
      addFirstWords: "Thêm những từ đầu tiên",
      aiSuggestHint: "Bạn cũng có thể để AI gợi ý các từ theo chủ đề",
      emptyOtherHint: "Muốn tự tạo bộ riêng? Tạo bộ sưu tập chỉ trong vài giây.",
      /** Dải tự động tạo từ chủ đề + thanh thao tác hàng loạt. */
      autoGeneratedFromTopic: "Tự động tạo từ chủ đề",
      /** Thanh thao tác hàng loạt: số lượng và hậu tố này là hai phần riêng. */
      selectedSuffix: "đã chọn",
      viewTopic: "Xem chủ đề",
      removeFromCollection: "Xoá khỏi bộ sưu tập",
      /** Xác nhận xoá. */
      deleteTitle: "Xoá bộ sưu tập",
      deleteDescription:
        'Bạn có chắc muốn xoá "{{title}}" không? Hành động này không thể hoàn tác và mọi từ trong bộ sưu tập này sẽ bị xoá.',
      /** Thông báo. */
      sharedToGroupDescription: "Đã chia sẻ bộ sưu tập với nhóm.",
      removedFromGroupDescription: "Đã xoá bộ sưu tập khỏi nhóm.",
      importedTitle: "Đã nhập",
      importedDescription: "Bộ từ vựng đã được thêm vào từ đã lưu của bạn.",
      failedToImport: "Không thể nhập bộ từ vựng.",
      removedFromImports: "Đã xoá bộ sưu tập khỏi danh sách nhập của bạn.",
      failedToRemoveImport: "Không thể bỏ nhập bộ sưu tập.",
      wordRemoved: "Đã xoá từ khỏi bộ sưu tập.",
      failedToRemoveWord: "Không thể xoá từ.",
      failedToReorder: "Không thể sắp xếp lại các từ.",
      bulkRemoved_one: "Đã xoá {{count}} từ khỏi bộ sưu tập.",
      bulkRemoved_other: "Đã xoá {{count}} từ khỏi bộ sưu tập.",
      failedToRemoveWords: "Không thể xoá các từ.",
      deletedTitle: "Đã xoá",
      deletedDescription: '"{{title}}" đã được xoá.',
      failedToDeleteCollection: "Không thể xoá bộ sưu tập.",
      failedToCopyLink: "Không thể sao chép liên kết.",
    },
    /** Hộp thoại chia sẻ với nhóm học tập (`ShareCollectionDialog`). */
    share: {
      description: 'Thêm hoặc xoá "{{title}}" khỏi các nhóm học tập của bạn.',
      searchPlaceholder: "Tìm nhóm...",
      loadingGroups: "Đang tải nhóm...",
      notAMember: "Bạn cần là thành viên của một nhóm học tập trước.",
      noGroupMatches: 'Không có nhóm nào khớp với "{{search}}".',
      currentlyShared: "Đang chia sẻ",
    },
    /** `/vocabulary/collections/new` và `/vocabulary/collections/:id/edit`. */
    creator: {
      seoTitleCreate: "Tạo bộ sưu tập | Lyder Talk",
      seoTitleEdit: "Sửa bộ sưu tập | Lyder Talk",
      seoDescriptionCreate:
        "Tạo bộ sưu tập từ vựng mới bằng cách chọn từ đã lưu hoặc tạo từ mới ngay tại trang",
      seoDescriptionEdit: "Chỉnh sửa bộ sưu tập từ vựng của bạn",
      notFoundDescription: "Bộ sưu tập này không tồn tại hoặc đã bị xoá.",
      selectWordsTitle: "Hãy chọn từ",
      selectWordsDescription: "Chọn ít nhất một từ cho bộ sưu tập.",
      /** `CreateCollectionHero`. */
      editingChip: "Đang sửa bộ sưu tập",
      newChip: "Bộ sưu tập mới",
      editTitle: "Sửa {{title}}",
      createTitle: "Tạo một bộ sưu tập từ vựng",
      editDescription: "Cập nhật thông tin và các từ thuộc bộ này.",
      createDescription:
        "Sắp xếp các từ đã lưu thành một bộ theo chủ đề. Chọn từ trong thư viện hoặc tạo từ mới ngay tại đây.",
      /** Biểu mẫu thông tin (`CreateCollectionForm` / `BuildWorkspace`). */
      detailsHeading: "Thông tin",
      collectionHeading: "Bộ sưu tập",
      titleLabel: "Tiêu đề",
      titlePlaceholder: "ví dụ: Tiếng Anh kinh doanh, Luyện IELTS...",
      titleRequired: "Tiêu đề là bắt buộc",
      descriptionLabel: "Mô tả",
      descriptionPlaceholder: "Mô tả ngắn...",
      visibilityLabel: "Quyền hiển thị",
      publicHint: "Bộ sưu tập công khai xuất hiện trong thư viện cộng đồng và ai cũng có thể nhập.",
      privateHint: "Bộ sưu tập riêng tư chỉ bạn mới nhìn thấy.",
      /** `CreateCollectionToolbar`. */
      searchSelectedPlaceholder: "Tìm trong các từ đã chọn...",
      dragToReorderHint: "kéo để sắp xếp lại",
      /** `CreateCollectionEmptyState`. */
      noWordsSelected: "Chưa chọn từ nào",
      noWordsSelectedHint:
        'Chọn từ trong thư viện của bạn, hoặc chuyển sang "Thêm từ mới" để tạo các từ mới.',
      /** `BuildWorkspace`. */
      wordsSourceAria: "Nguồn từ",
      fromMyVocabulary: "Từ kho của tôi",
      addNewWords: "Thêm từ mới",
      noSavedWordsYet: "Chưa có từ nào đã lưu",
      yourDeck: "Bộ từ của bạn",
      /** `CollectionProgressRail` — nhãn nằm trong bảng ở module scope nên phải mang theo khoá. */
      phaseAria: "Giai đoạn: {{phase}}",
      phases: {
        build: { label: "Xây dựng", hint: "Tiêu đề + từ vựng" },
        review: { label: "Xem lại", hint: "Kiểm tra và lưu" },
      },
      /** `CollectionActionStrip`. */
      addATitle: "Thêm tiêu đề",
      addOneWord: "Thêm ít nhất một từ",
      readyToReview: "Sẵn sàng xem lại",
      addAWordToSave: "Thêm một từ ở bước Xây dựng để lưu",
      wordsReady_one: "{{count}} từ đã sẵn sàng",
      wordsReady_other: "{{count}} từ đã sẵn sàng",
      backToEditing: "Quay lại chỉnh sửa",
      reviewCollection: "Xem lại bộ sưu tập",
      updating: "Đang cập nhật…",
      creating: "Đang tạo…",
      updateCollection: "Cập nhật bộ sưu tập",
      createCollection: "Tạo bộ sưu tập",
      /** `ReviewDeck`. */
      untitledCollection: "Bộ sưu tập chưa đặt tên",
      yourCollection: "Bộ sưu tập của bạn",
      noWordsYet: "Chưa có từ nào. Quay lại bước Xây dựng và thêm vào.",
      /** `DiscardChangesGuard`. */
      discardTitle: "Bỏ những từ mới của bạn?",
      discardDescription:
        "Các từ thêm tại chỗ chưa được lưu cho tới khi bạn tạo bộ sưu tập. Rời khỏi đây lúc này là mất chúng.",
      discardConfirm: "Bỏ và rời đi",
      keepEditing: "Tiếp tục chỉnh sửa",
    },
  },
  noteEditor: {
    deleteNote: "Xoá ghi chú",
    editNote: "Sửa ghi chú",
    cancel: "Huỷ",
    save: "Lưu",
    bold: "In đậm",
    italic: "In nghiêng",
    underline: "Gạch chân",
    strikethrough: "Gạch ngang",
    inlineCode: "Mã nội tuyến",
    bulletList: "Danh sách gạch đầu dòng",
    numberedList: "Danh sách đánh số",
    /** Chữ mờ hiện dưới ô soạn thảo khi còn trống. */
    addNotePlaceholder: "Thêm ghi chú...",
    noteLabel: "Ghi chú",
  },
  vocabularyWords: {
    enrichment: {
      sentences: "Câu ví dụ",
      collocations: "Kết hợp từ",
      synonyms: "Từ đồng nghĩa",
      antonyms: "Từ trái nghĩa",
      inDeck: "Trong bộ từ của bạn",
    },
    detail: {
      previousWord: "Từ trước",
      nextWord: "Từ sau",
      incompleteInfo: "Thiếu thông tin",
    },
    adder: {
      placeholder: "Nhập một từ rồi nhấn Enter",
    },
    selector: {
      searchPlaceholder: "Tìm từ...",
      level: "Trình độ",
    },
  },
  vocabularyDeck: {
    tabMyWords: "Từ của tôi",
    tabCollections: "Bộ sưu tập",
    tabNotes: "Ghi chú",
    stats: {
      ariaLabel: "Thống kê từ vựng",
      dueForReview: "Đến hạn ôn",
      dueReadyHint: "Sẵn sàng ôn — đã đến hạn theo lặp lại ngắt quãng",
      dueNothingHint: "Chưa đến hạn — quay lại sau nhé",
      wordsSaved: "Từ đã lưu",
      completeHint: "{{count}} đã hoàn thành",
      reviewedToday: "Đã ôn hôm nay",
      needsEnrichment: "Cần bổ sung",
      enrichmentAria: "Từ cần bổ sung thông tin",
      last7Days: "7 ngày qua",
      reviewCount: "{{count}} lần ôn",
      reviewCount_other: "{{count}} lần ôn",
      ofTotal: "{{count}} / {{total}} · {{percent}}%",
    },
    streak: {
      active: "Chuỗi học {{count}} ngày",
      none: "Chưa có chuỗi nào",
      dayUnit_one: "ngày",
      dayUnit_other: "ngày",
      startToday: "Bắt đầu hôm nay",
      streakLabel: "Chuỗi ngày học",
    },
    tiers: {
      masteryProgress: "Tiến trình ghi nhớ",
      mastery: "Mức độ thành thạo",
      otherBucket: "Khác / thêm thủ công",
    },
    garden: {
      ariaLabel: "Khu vườn từ vựng",
      title: "Khu vườn của bạn",
      hint: "Chọn một luống để lọc bộ từ",
      inBloom: "Đã nở",
      bloomCount: "{{count}}/{{total}}",
      bedAria: "{{stage}}: {{count}} từ. Lọc bộ từ.",
    },
    empty: {
      ariaLabel: "Chưa có từ vựng đã lưu",
      title: "Bắt đầu bộ từ của bạn",
      browseTitle: "Khám phá chủ đề",
      browseDescription: "Tìm một chủ đề trò chuyện bạn thích",
      saveTitle: "Lưu từ",
      saveDescription: "Đánh dấu sao bất kỳ từ nào để thêm vào bộ từ cá nhân",
      reviewTitle: "Ôn mỗi ngày",
      reviewDescription: "Lặp lại ngắt quãng giúp từ vựng ghi lâu vào trí nhớ",
      cta: "Khám phá chủ đề",
      description: "Lưu từ từ mọi buổi luyện tập và chúng sẽ xuất hiện ở đây, sẵn sàng để ôn bằng lặp lại ngắt quãng.",
    },
    noResults: {
      title: "Không có từ nào khớp bộ lọc",
      description: "Hãy thử bỏ bớt bộ lọc hoặc tìm từ khoá khác.",
      clearFilters: "Xoá bộ lọc",
    },
    aiWork: {
      enriching: "Đang bổ sung từ vựng",
      adding: "Đang thêm từ vựng",
      aiWorking: "AI đang xử lý",
      words: "từ",
      now: "Bây giờ",
      ofTotal: "trên {{total}} {{unit}}",
      finishingUp: "Đang hoàn tất…",
      failed: "{{count}} lỗi",
      cancelEnrichment: "Huỷ bổ sung",
      cancelGeneration: "Huỷ tạo",
      progressLabel: "{{label}}: {{completed}} / {{total}} {{unit}}",
    },
    toolbar: {
      all: "Tất cả",
      due: "Đến hạn",
      reviewed: "Đã ôn",
      noReview: "Chưa ôn",
      incomplete: "Thiếu thông tin",
      searchPlaceholder: "Tìm từ, định nghĩa, ví dụ...",
      clearSearch: "Xoá tìm kiếm",
      viewMode: "Kiểu hiển thị",
      gridView: "Dạng lưới",
      listView: "Dạng danh sách",
      advancedFilters: "Bộ lọc nâng cao",
      allLevels: "Tất cả trình độ",
      allTypes: "Tất cả loại từ",
      clearCalendarDate: "Xoá ngày đã chọn",
      filterByReviewStatus: "Lọc theo trạng thái ôn tập",
      selectAll: "Chọn tất cả",
      clearSelection: "Bỏ chọn tất cả",
      activeFilters: "Bộ lọc đang dùng",
      clearAll: "Xoá tất cả",
      sortBy: "Sắp xếp theo",
      sortAlpha: "A-Z",
      sortNewest: "Mới nhất",
      sortOldest: "Cũ nhất",
      sortMostPracticed: "Luyện nhiều nhất",
      resultsCount: "{{count}} từ",
      lastReviewed: "Ôn lần cuối {{date}}",
      neverReviewed: "Chưa từng ôn",
      removeWord: "Gỡ từ {{word}}",
      selected: "Đã chọn {{count}}",
      advancedFiltersDescription: "Lọc theo trình độ, loại từ hoặc ngày",
      certLevel: "Trình độ",
      wordType: "Loại từ",
      showingOf_one: "Đang hiện {{filtered}} trên {{total}} thẻ",
      showingOf_other: "Đang hiện {{filtered}} trên {{total}} thẻ",
      levelChip: "Trình độ: {{level}}",
      typeChip: "Loại: {{type}}",
      calendarFilter: "Đang lọc: {{date}}",
      clearChip: "Xoá {{label}}",
    },
    unsave: {
      mascotLine: "Lydi muốn chắc chắn bạn thật sự muốn gỡ từ này",
      title: "Gỡ từ đã lưu?",
      descriptionTail: "sẽ bị gỡ khỏi bộ từ đã lưu của bạn, cùng với tiến độ ôn tập. Bạn có thể lưu lại bất cứ lúc nào.",
      remove: "Gỡ",
      keepIt: "Giữ lại",
    },
    personalNote: {
      placeholder: "Nhập",
      untitled: "Ghi chú chưa có tiêu đề",
      deleteAria: "Xoá ghi chú",
      deleteTitle: "Xoá ghi chú?",
      deleteDescription: "Bạn có chắc muốn xoá ghi chú này? Hành động này không thể hoàn tác.",
      delete: "Xoá",
      cancel: "Huỷ",
    },
    tierLabels: {
      new: "Mới",
      learning: "Đang học",
      reviewing: "Đang ôn",
      mastered: "Đã ghi nhớ",
    },
    tierDescriptions: {
      newDescription: "Mới khám phá",
      learningDescription: "Đang ghi nhớ",
      reviewingDescription: "Củng cố trí nhớ",
      masteredDescription: "Ghi nhớ lâu dài",
    },
    card: {
      viewDetails: "Xem chi tiết",
      practice: "Luyện tập",
    },
    calendar: {
      activeDays_one: "{{count}} ngày có hoạt động trong tháng",
      activeDays_other: "{{count}} ngày có hoạt động trong tháng",
      reviewedOnDay_one: "Đã ôn {{count}} từ",
      reviewedOnDay_other: "Đã ôn {{count}} từ",
      dueOnDay_one: "{{count}} từ đến hạn",
      dueOnDay_other: "{{count}} từ đến hạn",
      monthlyReviewed_one: "Tháng này đã ôn {{count}} từ",
      monthlyReviewed_other: "Tháng này đã ôn {{count}} từ",
      moreCount: "+{{count}} nữa",
      noReviews: "Không có lượt ôn nào trong ngày này",
      reviewedSection: "Đã ôn",
      dueSection: "Đến hạn ôn",
      reviewedTag: "đã ôn",
      dueTag: "đến hạn",
      fewer: "Ít hơn",
      more: "Nhiều hơn",
      upcoming: "Sắp tới",
    },
    due: {
      waitingAria: "Từ đang chờ được học",
      newWordsLabel: "Từ mới",
      neverStudied_one: "từ chưa từng học",
      neverStudied_other: "từ chưa từng học",
      nothingDue: "Hiện chưa có từ nào đến hạn ôn",
      shareOfDeck: "trong {{total}} từ của bạn",
      perSession: "~{{count}} từ mỗi buổi",
      sessionsToClear: "{{count}} buổi để hết",
      minutesToStart: "~{{count}} phút để bắt đầu",
      startLearning: "Bắt đầu học",
      reminderAria: "Nhắc ôn hàng ngày",
      dailyReview: "Ôn hàng ngày",
      readyForReview_one: "từ sẵn sàng để ôn",
      readyForReview_other: "từ sẵn sàng để ôn",
      minutesShort: "~{{count}} phút",
      acrossTopics_one: "trên {{count}} chủ đề",
      acrossTopics_other: "trên {{count}} chủ đề",
      streakDays: "chuỗi {{count}} ngày",
      reviewEverything: "Ôn tất cả ({{count}})",
      dueCount: "{{count}} đến hạn",
      review: "Ôn",
      showFewer: "Thu gọn",
      moreTopics_one: "thêm {{count}} chủ đề",
      moreTopics_other: "thêm {{count}} chủ đề",
    },
    addDialog: {
      title: "Thêm từ vựng",
      description: "Thêm từ từng cái một. AI sẽ tạo định nghĩa, ví dụ và nhiều nội dung hơn cho mỗi từ.",
      descriptionImport: "Nhập từ thủ công, tạo định nghĩa bằng AI hoặc nhập từ file Excel/Sheets.",
      readyCount_one: "{{count}} từ sẵn sàng tạo",
      readyCount_other: "{{count}} từ sẵn sàng tạo",
      stop: "Dừng",
      generate: "Tạo",
    },
    popover: {
      saved: "Đã lưu",
      savedToast: "Đã lưu từ!",
      savedDescription: '"{{word}}" đã được thêm vào bộ từ của bạn.',
      failedToSave: "Không lưu được",
      couldNotSave: "Không thể lưu từ vựng",
      resources: "Tài nguyên",
      askCoach: "Hỏi trợ lý",
    },
    page: {
      seoTitle: "Từ vựng | Lyder Talk",
      seoDescription: "Luyện tập bộ từ đã lưu của bạn bằng thẻ ghi nhớ và câu hỏi",
      heroTitle: "Từ vựng của tôi",
      heroDescription: "Xây dựng bộ từ cá nhân và ghi nhớ từ vào trí nhớ dài hạn bằng lặp lại ngắt quãng.",
      badgeFlashcards: "Thẻ ghi nhớ",
      badgeFlashcardsDescription: "Thẻ tương tác chạm để lật, kèm phát âm thanh và ví dụ trong ngữ cảnh.",
      badgeSpaced: "Lặp lại ngắt quãng",
      badgeSpacedDescription: "Lịch ôn thông minh điều chỉnh theo khả năng ghi nhớ từng từ của bạn.",
      badgeEnrichment: "Bổ sung bằng AI",
      badgeEnrichmentDescription: "Tự động điền nghĩa, ví dụ và cách phát âm còn thiếu bằng AI.",
      addWord: "Thêm từ",
      incompleteWords_one: "từ thiếu thông tin",
      incompleteWords_other: "từ thiếu thông tin",
      incompleteHint: "AI tự điền cách phát âm, loại từ, trình độ CEFR và ví dụ",
      fillAll: "Bổ sung tất cả",
      gapContext: "Câu ví dụ theo ngữ cảnh",
      gapPronunciation: "Cách phát âm",
      gapExamples: "Ví dụ",
      gapCertLevel: "Trình độ CEFR",
      gapWordType: "Loại từ",
      gapRelated: "Từ liên quan",
      enrichmentComplete: "Đã bổ sung xong",
      enriched_one: "Đã cập nhật {{count}} từ",
      enriched_other: "Đã cập nhật {{count}} từ",
      failedSuffix: ", {{count}} lỗi",
      removed: "Đã gỡ",
      removedDescription: '"{{word}}" đã được gỡ khỏi bộ từ đã lưu của bạn.',
      failedToRemove: "Không thể gỡ từ vựng.",
      deleted_one: "Đã xoá {{count}} từ.",
      deleted_other: "Đã xoá {{count}} từ.",
      failedToDeleteSome: "Không thể xoá một số từ.",
      calendarSection: "Lịch",
      showingLoaded: "Đang hiện {{shown}} trên {{total}}",
      loadMore: "Tải thêm ({{count}} còn lại)",
      selectionWord_one: "từ đã chọn",
      selectionWord_other: "từ đã chọn",
      deleteWordsTitle: "Xoá các từ đã chọn",
      deleteWordsDescription_one: "Bạn có chắc muốn xoá {{count}} từ đã chọn? Hành động này không thể hoàn tác.",
      deleteWordsDescription_other: "Bạn có chắc muốn xoá {{count}} từ đã chọn? Hành động này không thể hoàn tác.",
      deleteWordTitle: "Xoá từ vựng",
      deleteWordDescription: 'Bạn có chắc muốn xoá "{{word}}"? Hành động này không thể hoàn tác.',
    },
  },
  myTopics: {
    questionsChipDescription: "Câu hỏi thảo luận để luyện kỹ năng nói",
    vocabularyChipDescription: "Từ và cụm từ chính với định nghĩa và ví dụ",
    speakingGuideChipDescription: "Cách diễn đạt tự nhiên, câu bắt đầu, và mẹo ngữ pháp",
    view: "Xem",
    shareToGroup: "Chia sẻ với nhóm",
    removeBanner: "Gỡ ảnh bìa",
    copied: "Đã sao chép",
    copyLink: "Sao chép liên kết",
    private: "Riêng tư",
    public: "Công khai",
    addBanner: "Thêm ảnh bìa",
    heroTitle: "Hôm nay bạn muốn luyện gì?",
    heroDescription: "Tìm chủ đề đã có của bạn, hoặc tạo chủ đề mới để bắt đầu luyện tập.",
    searchPlaceholder: "Tìm chủ đề của bạn...",
    allCategories: "Tất cả chủ đề",
    clearFilters: "Xoá bộ lọc",
    quickGenerateTitle: "Tạo nhanh",
    quickGenerateDescription: "Nhập một chủ đề để tạo nội dung",
    chatWithAiTitle: "Trò chuyện với AI",
    chatWithAiDescription: "Khám phá chủ đề một cách tự nhiên",
    searchResults: "Kết quả tìm kiếm",
    recentTopics: "Chủ đề gần đây",
    noSearchResults: "Không tìm thấy chủ đề nào phù hợp.",
    noTopicsYet: "Chưa có chủ đề nào. Hãy tạo một chủ đề mới!",
    loading: "Đang tải...",
    showMore: "Xem thêm",
    pin: "Ghim",
    unpin: "Bỏ ghim",
    delete: "Xoá",
    deleteTopicTitle: "Xoá chủ đề",
    deleteTopicDescription: 'Bạn có chắc muốn xoá "{{topic}}"? Hành động này không thể hoàn tác.',
    topicDeleted: "Đã xoá chủ đề",
    failedToDeleteTopic: "Không thể xoá chủ đề",
    signInRequired: "Cần đăng nhập",
    signInToGenerate: "Hãy đăng nhập để tạo nội dung luyện tập",
    failedToCreateTopic: "Không thể tạo chủ đề. Vui lòng thử lại.",
    streakProtectedByFreeze: "Chuỗi ngày của bạn đã được bảo vệ bởi một lần đóng băng!",
    unfinishedAria: "Các chủ đề chưa tạo xong nội dung",
    unfinishedTopics_one: "1 chủ đề chưa tạo xong",
    unfinishedTopics_other: "{{count}} chủ đề chưa tạo xong",
    unfinishedSingleDescription: "“{{topic}}” còn thiếu {{items}}.",
    unfinishedManyDescription: "Đang thiếu câu hỏi hoặc từ vựng. Hãy bắt đầu với “{{topic}}”.",
    unfinishedCardQuestions: "Chưa có câu hỏi",
    unfinishedCardVocabulary: "Chưa có từ vựng",
    missingQuestions: "câu hỏi",
    missingVocabulary: "từ vựng",
    missingQuestionsAndVocabulary: "câu hỏi và từ vựng",
    nothingGeneratedYet: "Chưa có nội dung",
    finishGenerating: "Tạo nốt nội dung",
    finishLatest: "Tạo nốt chủ đề mới nhất",
    finishCardAction: "Tạo nốt",
    finishGeneratingTopic: "Tạo nốt nội dung cho {{topic}}",
    generatingContent: "Đang tạo nội dung…",
    quickGenerate: {
      placeholder: "Ví dụ: Phỏng vấn việc, Đặt cà phê...",
      submit: "Tạo chủ đề nhanh",
      introGenerate: "Nhập tên chủ đề để tạo câu hỏi luyện tập, từ vựng và hướng dẫn phát biểu.",
      introRegenerate: "Chọn những loại nội dung bạn muốn tạo lại cho chủ đề này.",
      generateSubtitle: "Bắt đầu từ một chủ đề — AI sẽ soạn câu hỏi, từ vựng và hướng dẫn nói.",
      regenerateSubtitle: "Chọn nội dung cần tạo lại. Phần bỏ chọn sẽ giữ nguyên nội dung hiện có.",
      topicLabel: "Chủ đề",
      questionsDescription: "Câu hỏi để luyện nói",
      vocabularyDescription: "Từ chính kèm định nghĩa và ví dụ",
      guideDescription: "Cách diễn đạt tự nhiên, câu mở đầu và ngữ pháp",
      selectedCount: "Đã chọn {{count}}/3",
      generateContent: "Tạo nội dung",
      regenerateContent: "Tạo lại nội dung",
      practiceQuestions: "Câu hỏi luyện tập",
      keyVocabulary: "Từ vựng chính",
      speakingGuide: "Hướng dẫn nói",
      whatYoullGet: "Bạn sẽ có",
      selectToRegenerate: "Chọn nội dung cần tạo lại",
      generate: "Tạo",
      regenerate: "Tạo lại",
      generating: "Đang tạo...",
    },
    sidebar: {
      hidePanel: "Ẩn bảng bên",
      showPanel: "Hiện bảng bên",
      hidePanelAria: "Ẩn bảng chủ đề và cuộc trò chuyện",
      showPanelAria: "Hiện bảng chủ đề và cuộc trò chuyện",
      topicsTitle: "Chủ đề",
      newTopic: "Chủ đề mới",
      searchTopics: "Tìm chủ đề...",
      pinned: "Đã ghim",
      bookmarked: "Đã lưu",
      history: "Lịch sử",
      noMatchingTopics: "Không có chủ đề phù hợp",
      noHistoryYet: "Chưa có lịch sử",
      conversationsTitle: "Cuộc trò chuyện",
      newChat: "Cuộc trò chuyện mới",
      searchConversations: "Tìm cuộc trò chuyện...",
      noConversationsFound: "Không tìm thấy cuộc trò chuyện",
      noConversationsYet: "Chưa có cuộc trò chuyện",
      moreOptions: "Tùy chọn khác",
      timeToday: "Hôm nay",
      timeYesterday: "Hôm qua",
      timeThisWeek: "Tuần này",
      timeThisMonth: "Tháng này",
      timeOlder: "Trước đó",
      deleteConversationTitle: "Xoá cuộc trò chuyện?",
      deleteConversationDescription: "Hành động này không thể hoàn tác. Cuộc trò chuyện sẽ bị xoá vĩnh viễn.",
      cancel: "Huỷ",
      conversationUpdated: "Đã cập nhật cuộc trò chuyện",
      conversationDeleted: "Đã xoá cuộc trò chuyện",
      failedToUpdateConversation: "Không thể cập nhật cuộc trò chuyện",
      failedToDeleteConversation: "Không thể xoá cuộc trò chuyện",
    },
    ai: {
      newConversation: "Cuộc trò chuyện mới",
      newChat: "Trò chuyện mới",
      failedToRename: "Không thể đổi tên cuộc trò chuyện. Vui lòng thử lại.",
      topicSaved: "Đã lưu chủ đề!",
      failedToCreateTopic: "Không thể tạo chủ đề. Vui lòng thử lại.",
    },
    progress: {
      docTitle: "Tiến trình của bạn - Chủ đề của tôi",
      eyebrow: "Lịch sử luyện tập",
      title: "Tiến trình của bạn",
      description: "Mức độ bạn đã luyện tập và giọng trả lời của bạn thế nào.",
      seoDescription: "Xem bạn đã dành bao nhiêu thời gian luyện tập và nghe lại các câu trả lời đã ghi âm.",
      timeTab: "Thời gian học",
      recordingsTab: "Bản ghi âm",
      timeLabel: "Theo dõi thời gian",
      timeDescription: "Tự động ghi lại thời gian bạn dành cho mỗi buổi luyện tập, câu đố và ôn tập.",
      recordingsLabel: "Bản ghi âm",
      recordingsDescription: "Nghe lại mọi câu trả lời nói kèm nhận xét từ AI.",
    },
    chat: {
      messagePlaceholder: "Nhắn với trợ lý AI…",
      addTools: "Thêm công cụ và tệp đính kèm",
      attachFiles: "Đính kèm tệp",
      documentsFormat: "Tài liệu PDF, TXT hoặc MD",
      assistantCanAlso: "Trợ lý cũng có thể tìm kiếm trên web và đọc các liên kết bạn dán.",
      downloadConversation: "Tải xuống cuộc trò chuyện",
      toSend: "để gửi",
      forNewLine: "để xuống dòng",
      stopGenerating: "Dừng tạo",
      creatingConversation: "Đang tạo cuộc trò chuyện",
      sendMessage: "Gửi tin nhắn",
      eyebrow: "Công cụ tạo chủ đề AI",
      headline: "Hôm nay bạn muốn luyện gì?",
      lede: "Mô tả một chủ đề và tôi sẽ soạn câu hỏi, từ vựng, và hướng dẫn nói — rồi ta có thể bắt đầu nói to ngay.",
      startWithThese: "Bắt đầu với một trong các gợi ý sau",
      native: "Tiếng mẹ đẻ: {{language}}",
      interests: "Sở thích: {{list}}",
      goal: "Mục tiêu: {{goal}}",
      searchesWeb: "Tìm kiếm trên web",
      readsLinks: "Đọc liên kết bạn dán",
      acceptsFiles: "Nhận tệp PDF & TXT",
      suggestions: {
        dailyRoutines: "Thói quen hằng ngày",
        dailyRoutinesDesc: "Thói quen, lịch sinh hoạt và cuộc sống đời thường",
        travelPlans: "Lên kế hoạch du lịch",
        travelPlansDesc: "Chuyến đi, đặt vé và địa điểm mới",
        foodCooking: "Ẩm thực & nấu ăn",
        foodCookingDesc: "Công thức, nhà hàng và hương vị",
        jobInterviews: "Phỏng vấn việc làm",
        jobInterviewsDesc: "Sự nghiệp, mục tiêu và ấn tượng đầu tiên",
        moviesTv: "Phim ảnh & TV",
        moviesTvDesc: "Phim dài tập, thể loại và đề xuất",
        technology: "Công nghệ",
        technologyDesc: "Thiết bị, ứng dụng và tương lai",
      },
    },
    aiPreferences: {
      ariaLabel: "Tùy chọn AI",
      heading: "AI biết gì về bạn",
      subtitle: "Học tự động từ các cuộc trò chuyện của bạn — chỉnh sửa bất kỳ lúc nào.",
      loading: "Đang tải tùy chọn…",
      englishLevel: "Trình độ tiếng Anh",
      selectLevel: "Chọn trình độ",
      nativeLanguage: "Tiếng mẹ đẻ",
      nativeLanguagePlaceholder: "Ví dụ: Tiếng Việt, Tiếng Tây Ban Nha",
      interests: "Sở thích",
      interestsLabel: "Sở thích (tách bằng dấu phẩy)",
      interestsPlaceholder: "Ví dụ: nấu ăn, du lịch, công nghệ",
      practiceGoal: "Mục tiêu luyện tập",
      practiceGoalPlaceholder: "Ví dụ: đạt IELTS 7.0",
      save: "Lưu",
      clear: "Xoá hết",
      edit: "Chỉnh sửa",
      emptyState: "Chưa có gì được lưu. Hãy cho AI biết trình độ, tiếng mẹ đẻ hoặc sở thích của bạn — AI sẽ ghi nhớ tại đây.",
      setManually: "Thiết lập tùy chọn thủ công",
      recentLearning: "Mới học được",
      learnedFromChat: "Học từ cuộc trò chuyện",
      setManuallyShort: "Đặt thủ công",
      autoLearned: "Tự học",
      autoLearnedAt: "Tự học · {{time}}",
      goal: "Mục tiêu",
      secondsAgo: "{{count}} giây trước",
      minutesAgo: "{{count}} phút trước",
      hoursAgo: "{{count}} giờ trước",
      daysAgo: "{{count}} ngày trước",
      saved: "Đã lưu tùy chọn",
      savedDescription: "AI sẽ dùng các thông tin này trong các cuộc trò chuyện sau.",
      savedDescriptionAgain: "Đã lưu. AI sẽ dùng các thông tin này trong các cuộc trò chuyện sau.",
      saveFailed: "Không thể lưu tùy chọn. Vui lòng thử lại.",
      cleared: "Đã xoá tùy chọn",
      clearedDescription: "AI sẽ tự học lại trình độ và sở thích của bạn từ các cuộc trò chuyện sau.",
      clearFailed: "Không thể xoá tùy chọn. Vui lòng thử lại.",
    },
    generatingView: {
      generatingQuestions: "Đang tạo câu hỏi",
      generatingVocabulary: "Đang tạo từ vựng",
      generatingSpeakingGuide: "Đang tạo hướng dẫn nói",
      generatingYourTopic: "Đang tạo chủ đề của bạn",
      finishingUp: "Đang hoàn tất…",
      warmingUp: "Đang khởi động…",
      cancelGeneration: "Huỷ tạo nội dung",
      ready: "Sẵn sàng",
      queued: "Đang chờ",
      starting: "Đang bắt đầu…",
      working: "Đang xử lý…",
      questionsReady: "câu hỏi đã tạo",
      wordsFound: "từ đã tìm thấy",
      guideComplete: "hướng dẫn hoàn tất",
    },
    bannerDialog: {
      title: "Chọn ảnh bìa",
      subtitle: "Tìm ảnh miễn phí bản quyền hoặc tải ảnh của bạn lên.",
      searchTab: "Tìm ảnh",
      uploadTab: "Tải lên",
      searchPlaceholder: 'Tìm hình cho "{{topic}}"...',
      searchFailed: "Không thể tìm kiếm",
      noResults: "Không tìm thấy hình nào. Hãy thử từ khoá khác.",
      selected: "Đã chọn:",
      useThisImage: "Dùng ảnh này",
      clickToBrowse: "Nhấn để chọn tệp",
      uploadHint: "JPG, PNG, GIF, WebP — tối đa 5MB",
      previewAlt: "Xem trước",
      willReplace: "Ảnh bìa hiện tại sẽ bị thay thế.",
      clear: "Xoá chọn",
      tryAgain: "Thử lại",
      searchUnavailable: "Hiện không thể tìm ảnh. Vui lòng thử lại.",
      invalidFileType: "Tệp này không phải ảnh dùng được. Hãy chọn JPG, PNG, GIF hoặc WebP.",
      fileTooLarge: "Ảnh lớn hơn 5MB. Hãy chọn tệp nhỏ hơn.",
      attributionPrefix: "Ảnh của",
    },
    detail: {
      notFound: "Không tìm thấy chủ đề hoặc bạn không có quyền truy cập",
      goBack: "Quay lại Chủ đề của tôi",
      shared: "Đã chia sẻ",
      sharedToGroup: "Đã chia sẻ chủ đề với nhóm.",
      removed: "Đã gỡ",
      removedFromGroup: "Đã gỡ chủ đề khỏi nhóm.",
      failedToShare: "Không thể chia sẻ.",
      failedToRemove: "Không thể gỡ.",
      madePublicTitle: "Đã công khai phiên!",
      madePublicDescription: "Phiên này giờ đã hiển thị với mọi người",
      failedToMakePublic: "Không thể công khai phiên",
      linkCopiedTitle: "Đã sao chép liên kết!",
      linkCopiedDescription: "Chia sẻ liên kết này với người khác",
      categoryUpdated: "Đã cập nhật chủ đề",
      categoryChanged: "Đã đổi chủ đề sang {{category}}",
      failedToUpdateCategory: "Không thể cập nhật chủ đề",
      pinnedTitle: "Đã ghim phiên",
      unpinnedTitle: "Đã bỏ ghim phiên",
      pinnedDescription: "Phiên này sẽ xuất hiện trong danh sách đã ghim",
      unpinnedDescription: "Đã gỡ phiên khỏi danh sách đã ghim",
      alwaysPublicTitle: "Chủ đề luôn công khai",
      alwaysPublicDescription: "Chia sẻ luôn được bật để mọi người có thể học từ chủ đề của bạn.",
      madePublicToast: "Đã công khai",
      failedToUpdateSession: "Không thể cập nhật phiên",
      sessionDeleted: "Đã xoá phiên",
      sessionDeletedDescription: "Phiên luyện tập đã được gỡ bỏ",
      failedToDeleteSession: "Không thể xoá phiên",
      bannerSaved: "Đã lưu ảnh bìa",
      bannerReplaced: "Ảnh bìa đã được thay thế.",
      bannerUploaded: "Ảnh bìa đã được tải lên.",
      unsplashBannerSet: "Đã đặt ảnh bìa từ Unsplash.",
      uploadFailed: "Tải lên thất bại",
      failedToUploadBanner: "Không thể tải ảnh bìa lên.",
      failedToSaveBannerTitle: "Không thể lưu ảnh bìa",
      failedToSaveBanner: "Không thể lưu ảnh bìa.",
      bannerRemoved: "Đã gỡ ảnh bìa",
      bannerRemovedDescription: "Ảnh bìa của chủ đề đã được gỡ bỏ",
      failedToRemoveBanner: "Không thể gỡ ảnh bìa",
    },
  },
  speak: {
    idle: "Nghe",
    speaking: "Dừng",
  },
  notifications: {
    dismiss: "Ẩn thông báo",
    header: "Thông báo",
    markAllRead: "Đánh dấu đã đọc tất cả",
    loading: "Đang tải thông báo...",
    noNotifications: "Không có thông báo",
    noTabNotifications: "Không có thông báo {{tab}}",
    emptyAll: "Thông báo sẽ hiện ở đây khi có hoạt động trong các nhóm học của bạn",
    emptyUpdates: "Hãy quay lại sau để xem cập nhật và thông báo sản phẩm mới",
    emptyOther: "Thử xem các tab khác để biết thêm thông báo",
    deleteTitle: "Xoá thông báo",
    deleteDescription: "Bạn có chắc muốn xoá thông báo này? Hành động này không thể hoàn tác.",
    deleteConfirm: "Xoá",
    tabs: {
      all: "Tất cả",
      activity: "Hoạt động",
      social: "Mạng xã hội",
      invites: "Lời mời",
      updates: "Cập nhật",
    },
    categories: {
      feature: "Tính năng mới",
      improvement: "Cải thiện",
      fix: "Sửa lỗi",
      announcement: "Thông báo",
    },
  },
  topicTabs: {
    all: "Tất cả",
    recent: "Gần đây",
    trending: "Thịnh hành",
    popular: "Phổ biến",
    mostBookmarked: "Đã lưu",
  },
  videos: {
    title: "Học qua video",
    resultsHeading: "Kết quả video",
    description: "Học tiếng Anh với video thật từ YouTube, TikTok, Instagram và nhiều hơn nữa",
    eyebrow: "Xem & nhại lại",
    metaLabel: "Phụ đề tương tác",
    metaDescription: "Nhấp vào bất kỳ từ nào trong phụ đề để dịch, lưu vào từ vựng, hoặc nhảy tới đúng khoảnh khắc đó trong video.",
    metaLabelTwo: "Luyện nhại theo",
    metaDescriptionTwo: "Ghi âm lại phần phụ đề để cải thiện cách phát âm, nhịp điệu và độ trôi chảy.",
    libraryLabel: "Thư viện",
    videoLessons: "Bài học video",
    videosLabel: "Bài học video",
    videoSingular: "video",
    videoPlural: "video",
    categorySingular: "chuyên mục",
    categoryPlural: "chuyên mục",
    searchPlaceholder: "Tìm video...",
    clearSearch: "Xoá tìm kiếm",
    certLevelAria: "Lọc theo trình độ CEFR",
    requestVideo: "Yêu cầu video",
    browseTopics: "Khám phá chủ đề",
    suggestHint: "Không thấy video bạn muốn? Hãy gợi ý và chúng tôi sẽ thêm vào.",
    addedThisWeek: "Thêm trong tuần này",
    seoTitle: "Học qua video | Lyder Talk",
    seoDescription: "Học tiếng Anh bằng video có phụ đề tương tác và bài luyện nhại theo",
    videoStatsLabel: "Thống kê thư viện video",
    inContext: {
      entry: "Tìm trong ngữ cảnh",
      title: "Trong ngữ cảnh",
      description: "Gõ một từ hoặc cụm từ và nghe nó được nói trong chính những video bạn đang học. Mỗi kết quả là một khoảnh khắc thật trong video thật, không phải một định nghĩa.",
      eyebrow: "Xem & nhại lại",
      backToLibrary: "Về thư viện",
      searchPlaceholder: "Gõ một từ hoặc cụm từ…",
      searchLabel: "Tìm trong phụ đề video",
      searchAction: "Tìm",
      clearSearch: "Xoá tìm kiếm",
      sourceLabel: "Tìm trong",
      sourceAll: "Tất cả video",
      sourceLibrary: "Thư viện",
      sourceImported: "Đã nhập",
      transport: {
        clipGroup: "Đoạn",
        playbackGroup: "Phát lại",
        previous: "Đoạn trước",
        next: "Đoạn sau",
        back: "Lùi 5 giây",
        forward: "Tiến 5 giây",
        play: "Phát",
        pause: "Tạm dừng",
        replay: "Nói lại lần nữa",
        loop: "Lặp lại đoạn này",
        speed: "Tốc độ phát",
      },
      prompt: "Gõ ít nhất {{min}} ký tự để tìm trong toàn bộ phụ đề của thư viện.",
      searching: "Đang tìm trong phụ đề…",
      error: "Tạm thời không tìm được trong phụ đề. Hãy thử lại sau giây lát.",
      noResults: "Chưa có video nào trong thư viện nói cụm từ này.",
      noResultsHint: "Hãy thử một từ thông dụng — tìm kiếm khớp nguyên từ, nên cụm từ phải xuất hiện đúng như khi được nói.",
      videoSingular: "video",
      videoPlural: "video",
      openVideo: "Mở bài học đầy đủ",
      loadMore: "Xem thêm đoạn khác",
      opensOn: "Mở trên {{platform}} — ứng dụng chưa nhảy tới đúng khoảnh khắc đó được",
      seoTitle: "Trong ngữ cảnh | Lyder Talk",
      seoDescription: "Nghe bất kỳ từ hoặc cụm từ nào được nói trong video thật của thư viện, đúng khoảnh khắc nó được nói.",
      wordDialog: {
        description: "Nghe từ này được nói trong video thật, đúng khoảnh khắc nó xuất hiện. Hãy giảm tốc độ và nhại lại theo câu đó.",
        empty: "Chưa có video nào trong thư viện nói từ này.",
        allClips: "Mở trong mục Trong ngữ cảnh để xem mọi đoạn",
      },
    },
  },
  leaderboard: {
    title: "Bảng xếp hạng",
    description: "Xem việc luyện tập của bạn so với các bạn học khác.",
    eyebrow: "Cộng đồng",
    highlights: "Nổi bật của bảng xếp hạng",
    topRanking: "Xếp hạng tổng",
    topRankingDescription: "Người có thành tích tổng thể tốt nhất",
    topStudyTime: "Thời gian học tập",
    topStudyTimeDescription: "Người luyện tập tích cực nhất",
    topVocabulary: "Từ vựng được ôn nhiều nhất",
    topVocabularyDescription: "Từ vựng được ôn tập nhiều nhất",
    topLevels: "Cấp độ nổi bật",
    highestLevels: "Cấp độ cao nhất",
    topStreaks: "Chuỗi ngày nổi bật",
    topStreaksDesc: "Những chuỗi ngày dài nhất",
    noStreaks: "Chưa có chuỗi ngày nào",
    streakHint: "Luyện tập hằng ngày để xây dựng chuỗi ngày của bạn!",
    noLevels: "Chưa có cấp độ nào",
    levelsHint: "Bắt đầu tích XP để thăng cấp!",
    startVocab: "Bắt đầu học từ vựng!",
    longestStreak: "Chuỗi ngày dài nhất",
    mostTime: "Thời gian nhiều nhất",
    topVocabularyShort: "Từ vựng nổi bật",
    topXp: "XP cao nhất",
    level: "cấp",
    days: "ngày",
    reviews: "lượt ôn",
    rankings: "Xếp hạng",
    rankingsDesc: "Xem bạn xếp bao nhiêu so với người học khác trong tuần này, tháng này và mọi lúc.",
    streaks: "Chuỗi ngày",
    streaksDesc: "Số ngày luyện tập liên tiếp — giữ chuỗi để xây dựng thói quen.",
    achievements: "Thành tích",
    achievementsDesc: "Mở khóa huy hiệu và cột mốc khi bạn đạt được mục tiêu học tập mới.",
    heroDesc: "Tôn vinh những người học tận tâm nhất và những thành tựu của họ.",
    seoTitle: "Bảng xếp hạng - Người học và thành tích nổi bật",
    seoDesc: "Khám phá những người học nổi bật, xếp hạng theo thời gian học, cấp độ XP và chuỗi ngày.",
    noData: "Chưa có dữ liệu",
    noDataDescription: "Hãy quay lại sau nhé!",
    noRankings: "Chưa có bảng xếp hạng",
    noRankingsDescription: "Bắt đầu học để xuất hiện tại đây!",
    noLearners: "Chưa có người học nào",
    noLearnersDescription: "Bắt đầu luyện tập để xuất hiện tại đây!",
    noVocabulary: "Chưa có từ vựng nào",
    noVocabularyDescription: "Hãy lưu và ôn tập vài từ để xuất hiện tại đây!",
    overall: "tổng thể",
    levelLabel: "Cấp độ",
    timeLabel: "Thời gian",
    streakLabel: "Chuỗi ngày",
    reviewsUnit: "lượt ôn",
    wordsUnit: "từ",
    topicsUnit_other: "chủ đề",
    yourRank: "Hạng của bạn",
    compete: "Cạnh tranh",
    claimRank: "Hoàn thành một phiên để có hạng đầu tiên",
    signInRank: "Đăng nhập để theo dõi hạng của bạn",
    studyTimeTitle: "Thời gian học",
    vocabularyTitle: "Từ vựng",
    streaksTitle: "Chuỗi ngày",
    levelsTitle: "Cấp độ",
    fullRankings: "Bảng xếp hạng đầy đủ",
    levelShort: "Cấp {{level}}",
    periodsLabel: "Khoảng thời gian xếp hạng",
    periodToday: "Hôm nay",
    periodWeek: "Tuần này",
    periodMonth: "Tháng này",
    periodAll: "Mọi lúc",
    periodPhraseToday: "hôm nay",
    periodPhraseWeek: "tuần này",
    periodPhraseMonth: "tháng này",
    periodPhraseAll: "mọi lúc",
    metricsLabel: "Hạng mục xếp hạng",
    metricAll: "Tất cả",
    metricXp: "XP",
    metricTime: "Thời gian học",
    metricVocab: "Từ vựng",
    metricStreak: "Chuỗi ngày",
    rankedByXp: "Xếp hạng theo tổng XP",
    rankedByXpPeriod: "Xếp hạng theo XP kiếm được {{period}}",
    rankedByTime: "Xếp hạng theo tổng thời gian luyện tập",
    rankedByTimePeriod: "Xếp hạng theo thời gian luyện tập {{period}}",
    rankedByVocab: "Xếp hạng theo lượt ôn từ vựng",
    rankedByVocabPeriod: "Xếp hạng theo số từ thêm mới {{period}}",
    rankedByStreak: "Xếp hạng theo chuỗi ngày dài nhất",
    rankedByStreakPeriod: "Xếp hạng theo số ngày luyện tập {{period}}",
    unitXp: "XP",
    unitWord_other: "từ",
    unitReview_other: "lượt ôn",
    unitDay_other: "ngày",
    learnersRanked_other: "{{count}} người được xếp hạng",
    allBoardsSubtitle: "Tất cả bảng trong một cái nhìn",
    popularTopics: "Chủ đề nổi bật",
    popularTopicsSubtitle: "Những chủ đề được mở, thích và bình luận nhiều nhất.",
    viewFullBoard: "Xem toàn bộ bảng",
    yourRankByXp: "Hạng của bạn theo XP",
    yourRankByXpPeriod: "Hạng của bạn theo XP {{period}}",
    yourPosition: "Vị trí của bạn",
    yourRankPeriod: "Hạng của bạn {{period}}",
    you: "Bạn",
    showMoreLearners: "Xem thêm người học",
    allTimeOnlyNote: "Bảng xếp hạng theo kỳ cần bản cập nhật cơ sở dữ liệu mới nhất — tạm thời hiển thị kết quả mọi lúc.",
    emptyPeriodDescription: "Chưa ai ghi điểm {{period}}.",
    emptyHint: "Hoàn thành một phiên để xuất hiện trên bảng.",
    loadError: "Không tải được bảng xếp hạng",
    loadErrorHint: "Kiểm tra kết nối và thử lại sau giây lát.",
    streakDays_other: "{{count}} ngày",
  },
  studyGroups: {
    title: "Nhóm học",
    description: "Luyện cùng tài liệu với người học khác. Các nhóm chia sẻ chủ đề và bộ từ vựng, mỗi ngày chọn một mục tiêu.",
    metaLabel: "Luyện tập chung",
    metaDescription: "Mọi người đều đi qua cùng bộ chủ đề, nên bạn có thể so sánh câu trả lời và thấy mình bỏ sót gì.",
    metaLabelTwo: "Từ vựng chung",
    metaDescriptionTwo: "Bộ từ vựng của nhóm gộp những danh sách rời rạc thành một bộ thẻ duy nhất để luyện tập.",
    clearSearch: "Xóa tìm kiếm",
    signInTitle: "Đăng nhập để truy cập nhóm của bạn",
    signInDescription: "Tạo và quản lý nhóm học của riêng bạn, cùng học với người khác và theo dõi tiến độ.",
    joinSignInTitle: "Đăng nhập để tham gia nhóm này",
    joinSignInDescription: "Nhóm học cần có tài khoản để biết ai đã tham gia.",
    leaveTitle: "Rời nhóm",
    leaveDescription: "Bạn có chắc muốn rời nhóm này? Bạn sẽ cần được mời lại hoặc tham gia lại để quay vào.",
    openGroups: "Nhóm đang mở",
    myGroups: "Nhóm của tôi",
    youBelongTo: "bạn tham gia",
    members: "Thành viên",
    activeToday_other: "{{count}} nhóm hoạt động hôm nay",
    studyGroupStatsLabel: "Thống kê nhóm học",
    noDescription: "Chưa có mô tả — mở nhóm để xem các thành viên đang chia sẻ gì.",
    groupPublic: "Công khai",
    groupPrivate: "Riêng tư",
    groupOwner: "Chủ nhóm",
    groupAdmin: "Quản trị",
    groupMember: "Thành viên",
    groupFull: "Đã đầy",
    groupSeatsOpen: "Còn {{count}} chỗ",
    groupSeatsSr: "thành viên, còn chỗ trống",
    groupFullSr: "Nhóm đã đầy",
    createGroup: "Tạo nhóm",
    myGroupsTab: "Nhóm của tôi",
    allGroupsTab: "Tất cả nhóm",
    noneJoinedTitle: "Bạn chưa tham gia nhóm nào",
    noneJoinedDescription: "Hãy tạo nhóm của riêng bạn và chia sẻ chủ đề luyện tập với bạn học, hoặc tham gia một nhóm mở từ tab Tất cả nhóm.",
    createFirst: "Tạo nhóm đầu tiên",
    noMatchTitle: "Không có nhóm nào khớp tìm kiếm",
    noMatchDescription: "Hãy thử tên, mô tả hoặc thành viên khác — hoặc xem tất cả nhóm đang mở.",
    showingFound: "Hiển thị {{shown}} trong {{total}} nhóm",
    showingMine: "Bạn đã tham gia {{count}} nhóm",
    showingAll: "Hiển thị {{count}} nhóm",
    share: "Chia sẻ",
    shareCollections: "Chia sẻ {{count}} bộ sưu tập",
    shareTopics: "Chia sẻ {{count}} chủ đề",
    shareCollectionTitle: "Chia sẻ bộ sưu tập",
    shareCollectionDescription: "Chọn bộ sưu tập từ vựng để chia sẻ với nhóm",
    noCollectionsFound: "Không tìm thấy bộ sưu tập",
    noCollectionsYet: "Chưa có bộ sưu tập",
    noPublicCollections: "Không có bộ sưu tập công khai",
    createCollectionFirst: "Tạo một bộ sưu tập từ vựng trước để chia sẻ với nhóm",
    noPublicCollectionsFromOthers: "Không có bộ sưu tập công khai từ người dùng khác",
    myTopicsTab: "Chủ đề của tôi",
    communityTab: "Cộng đồng",
    shareSessionTitle: "Chia sẻ phiên",
    shareSessionDescription: "Chọn chủ đề để chia sẻ với nhóm",
    searchPlaceholder: "Tìm nhóm theo tên, chủ đề hoặc thành viên…",
    avatarUploadFailed: "Tạo nhóm thành công nhưng tải ảnh đại diện thất bại",
    createFailed: "Tạo nhóm học thất bại",
    createError: "Đã xảy ra lỗi khi tạo nhóm học",
    joinFailed: "Tham gia nhóm thất bại",
    leftSuccess: "Đã rời nhóm thành công",
    leaveFailed: "Rời nhóm thất bại",
    deleteSuccess: "Xóa nhóm thành công",
    deleteFailed: "Xóa nhóm thất bại",
    deleteTitle: "Xóa nhóm",
    deleteConfirm: "Bạn có chắc muốn xóa nhóm này? Hành động này không thể hoàn tác. Tất cả thành viên, phiên chia sẻ và dữ liệu liên quan sẽ bị xóa vĩnh viễn.",
    noPublicGroups: "Chưa có nhóm công khai nào",
    noPublicGroupsDesc: "Chưa ai mở nhóm cho cộng đồng. Hãy là người đầu tiên.",
    // Nhãn hành động và giao diện chi tiết nhóm học (trước đây là tiếng Anh cố định)
    join: "Tham gia",
    manage: "Quản lý",
    leave: "Rời nhóm",
    leaveGroup: "Rời nhóm",
    dialogDeleteSharedSessionTitle: "Xóa phiên chia sẻ",
    dialogDeleteSharedSessionDescription: "Bạn có chắc muốn xóa phiên chia sẻ này khỏi nhóm? Hành động này không thể hoàn tác.",
    dialogRemoveSharedCollectionTitle: "Xóa bộ sưu tập chia sẻ",
    dialogRemoveSharedCollectionDescription: "Bạn có chắc muốn xóa bộ sưu tập chia sẻ này khỏi nhóm? Hành động này không thể hoàn tác.",
    dialogTypeToConfirm: "Nhập {{name}} để xác nhận",
    dialogEnterGroupName: "Nhập tên nhóm",
    dialogGroupNameMismatch: "Tên nhóm không khớp",
    dialogDeleting: "Đang xóa...",
    dialogRemovingMemberTitle: "Xóa thành viên",
    dialogRemovingMemberDescription: "Bạn có chắc muốn xóa {{name}} khỏi nhóm? Hành động này không thể hoàn tác.",
    dialogRemoveMemberConfirm: "Xóa thành viên",
    clearFilters: "Xóa bộ lọc",
    shareTopic: "Chia sẻ chủ đề",
    shareAndSet: "Chia sẻ & đặt",
    setTopicOfDay: "Đặt chủ đề của ngày",
    shareCollection: "Chia sẻ bộ sưu tập",
    full: "Đầy",
    moreActionsFor: "Thao tác khác cho {{name}}",
    membersAndInvites: "Thành viên & lời mời",
    editGroup: "Sửa nhóm",
    deleteGroup: "Xóa nhóm",
    startedBy: "Tạo bởi {{name}}",
    noDescriptionDetail: "Chưa có mô tả — chủ nhóm chưa nói họ học gì.",
    joinGroup: "Tham gia nhóm",
    joinGroupToView: "Tham gia nhóm để xem các phiên và bộ sưu tập",
    groupFullLimit: "{{name}} đã đạt giới hạn {{max}} thành viên, nên tạm thời không nhận thành viên mới.",
    groupOpenMembership: "{{count}} / {{max}} thành viên đã tham gia. Ai cũng có thể vào nhóm.",
    groupInviteOnly: "{{name}} chỉ vào bằng lời mời. Hãy nhờ một thành viên gửi lời mời để xem họ đang học gì.",
    moreGroupActions: "Thao tác khác cho nhóm",
    membersCount: "Thành viên ({{count}})",
    sharedPracticeTopics: "Chủ đề luyện tập được chia sẻ",
    sharedVocabularyCollections: "Bộ từ vựng được chia sẻ",
    tabTopics: "Chủ đề",
    tabCollections: "Bộ sưu tập",
    sharedMaterial: "Tài liệu chia sẻ",
    searchTopics: "Tìm chủ đề…",
    searchCollections: "Tìm bộ sưu tập…",
    newest: "Mới nhất",
    mostViewed: "Xem nhiều nhất",
    mostLiked: "Thích nhiều nhất",
    showingCount: "Hiển thị {{shown}} / {{total}}",
    todayFocus: "Trọng tâm hôm nay",
    pickTopicTogether: "Chọn chủ đề cả nhóm học cùng",
    noTopicToday: "Chưa đặt chủ đề hôm nay",
    selectTopicHelp: "Chọn chủ đề để giúp nhóm tập trung luyện tập",
    practiceCalendar: "Lịch luyện tập",
    recentActivity: "Hoạt động gần đây",
    todaysPlans: "Kế hoạch hôm nay",
    noPlansForDay: "Chưa có kế hoạch cho ngày này — nhấn + để thêm",
    noPracticePlans: "Chưa có kế hoạch luyện tập cho ngày này",
    clearPlan: "Xóa kế hoạch",
    calendarToday: "Hôm nay",
    calendarWeek: "Tuần",
    calendarMonth: "Tháng",
    plan: "Lên kế hoạch",
    viewAllEvents: "Xem tất cả {{count}} sự kiện",
    currentTopicOfDay: "Chủ đề của ngày",
    setAsTopicOfDay: "Đặt làm chủ đề của ngày",
  },
  session: {
    breadcrumbTopics: "Chủ đề",
    questions: "Câu hỏi",
    inContext: {
      title: "Nghe những từ này trong ngữ cảnh",
      description: "Những đoạn thật trong thư viện video, nơi các từ của chủ đề này thực sự được nói.",
      action: "Tìm đoạn video",
      hide: "Ẩn",
      watchWord: "Xem",
      watchPhrase: "Xem: nghe cách nói này trong video",
      found: "Tìm thấy {{matched}} trên {{total}} từ trong video",
      loading: "Đang tìm trong phụ đề…",
      error: "Tạm thời không tìm được trong phụ đề. Hãy thử lại sau giây lát.",
      empty: "Chưa có video nào trong thư viện nói những từ này.",
      emptyHint: "Tìm kiếm khớp nguyên từ, nên một từ vừa tạo sẽ xuất hiện khi có video phù hợp được thêm vào.",
      truncated: "Đang xem {{count}} từ đầu tiên của chủ đề. Hãy tìm từng từ bên dưới cho phần còn lại.",
      allClips: "Tìm bất kỳ từ nào trong thư viện",
    },
    allQuestions: "Tất cả câu hỏi",
    closeQuestionList: "Đóng danh sách câu hỏi",
    vocabulary: "Từ vựng",
    reference: "Tham khảo",
    discussion: "Thảo luận",
    loadingDiscussion: "Đang tải thảo luận...",
    speakingGuide: "Hướng dẫn nói",
    guideExpressions: "Cụm tự nhiên",
    guideStarters: "Cách mở câu",
    guideFillers: "Từ nối",
    guideGrammar: "Ngữ pháp",
    noContentAvailable: "Không có nội dung",
    viewNote: "Xem ghi chú",
    like: "Thích",
    unlike: "Bỏ thích",
    bookmark: "Lưu",
    bookmarked: "Đã lưu",
    removeBookmark: "Bỏ lưu",
    edit: "Sửa",
    share: "Chia sẻ",
    moreActions: "Thao tác khác",
    rteBold: "In đậm",
    rteItalic: "In nghiêng",
    rteUnderline: "Gạch chân",
    rteStrikethrough: "Gạch ngang",
    rteInlineCode: "Mã nội tuyến",
    rteBulletList: "Danh sách gạch đầu dòng",
    rteNumberedList: "Danh sách đánh số",
    rteQuote: "Trích dẫn",
    rteAddLink: "Thêm liên kết",
    rteAddImage: "Thêm ảnh",
    rteImageUploading: "Đang tải ảnh…",
    rteImageAlt: "Ảnh đính kèm",
    rteImageRejected: "Không thêm được ảnh này",
    rteImageTooLarge: "Ảnh phải nhỏ hơn hoặc bằng 5MB.",
    rteImageWrongType: "Chỉ hỗ trợ ảnh JPEG, PNG, GIF hoặc WebP.",
    rteImageEmpty: "Tệp này bị rỗng.",
    rteImageFailed: "Không tải ảnh lên được",
    rteImageFailedHint: "Kiểm tra kết nối và thử lại.",
    rteMention: "Nhắc đến câu hỏi, từ hoặc cách diễn đạt",
    mentionPickerTitle: "Nhắc đến nội dung trong chủ đề",
    mentionEmpty: "Không có kết quả cho “{{query}}”",
    mentionQuestionsGroup: "Câu hỏi",
    mentionVocabularyGroup: "Từ vựng",
    mentionExpressionsGroup: "Cách diễn đạt",
    mentionHereNow: "Đang ở đây",
    mentionNavigate: "để di chuyển",
    mentionInsertHint: "để thêm",
    mentionOpen: "Mở “{{label}}”",
    viewImage: "Xem ảnh đầy đủ",
    imageViewerTitle: "Xem trước ảnh",
    rteSubmit: "Gửi",
    liveChat: "Trò chuyện trực tiếp",
    openLiveChat: "Mở trò chuyện trực tiếp",
    closeLiveChat: "Đóng trò chuyện trực tiếp",
    hereCount: "{{count}} người ở đây",
    chatToday: "Hôm nay",
    chatYesterday: "Hôm qua",
    chatEmptyTitle: "Chưa có tin nhắn nào",
    chatEmptyHint: "Chào mọi người nhé — cuộc trò chuyện được lưu cùng chủ đề, người học sau sẽ đọc được.",
    chatLoadingHistory: "Đang tải tin nhắn…",
    chatLoadEarlier: "Tải tin nhắn trước đó",
    chatLoadingEarlier: "Đang tải…",
    chatPlaceholder: "Nhập tin nhắn… (@ để nhắc đến)",
    chatMention: "Nhắc đến người hoặc nội dung",
    discussionPlaceholder: "Viết bình luận… (@ để nhắc đến)",
    chatEmoji: "Thêm biểu tượng cảm xúc",
    chatEmojiSearch: "Tìm biểu tượng",
    chatSend: "Gửi tin nhắn",
    chatSignIn: "Đăng nhập để tham gia trò chuyện",
    chatConnecting: "Đang kết nối…",
    chatSending: "Đang gửi…",
    chatFailed: "Chưa gửi được",
    chatYou: "Bạn",
    chatLearner: "Học viên",
    chatUnread: "{{count}} tin nhắn chưa đọc",
    expandChat: "Mở rộng trò chuyện",
    collapseChat: "Thu nhỏ trò chuyện",
    dragToMove: "Kéo để di chuyển",
    resetChatPosition: "Đặt lại vị trí trò chuyện",
    saveToVocabulary: "Lưu vào từ vựng",
    removeFromSaved: "Bỏ lưu",
    noDiscussionYet: "Chưa có thảo luận nào",
    beFirstToShare: "Hãy là người đầu tiên chia sẻ suy nghĩ của bạn!",
    comments: "Bình luận",
    practiceWithAi: "Luyện tập với AI",
    aiDescription: "Trò chuyện về chủ đề này với gia sư AI",
    startPractice: "Bắt đầu luyện tập",
    chooseMode: "Chọn chế độ",
    viewTopic: "Xem chủ đề",
    questionsVocabAtAGlance: "Câu hỏi & từ vựng tổng quan",
    speakWithAiFeedback: "Luyện nói với phản hồi AI theo thời gian thực",
    practiceVocabulary: "Luyện từ vựng",
    vocabularyDescription: "Ôn và ghi nhớ {{count}} từ trong chủ đề này",
    studyWords: "Học từ vựng",
    letsGetStarted: "Bắt đầu thôi!",
    expandStudyGuide: "Mở hướng dẫn học",
    collapseStudyGuide: "Thu gọn hướng dẫn học",
    guideSteps_one: "{{count}} bước",
    guideSteps_other: "{{count}} bước",
    guideExampleHint: "Câu trả lời mẫu",
    guideKeyPointsHint_one: "{{count}} ý chính",
    guideKeyPointsHint_other: "{{count}} ý chính",
    exampleLabel: "Ví dụ",
    greatStart: "Khởi đầu tốt lắm!",
    keepItUp: "Tiếp tục nào!",
    almostThere: "Sắp tới rồi!",
    soClose: "Gần thành công!",
    wellDone: "Tuyệt vời!",
    done: "Xong",
    words: "Từ",
    time: "Thời gian",
    completed: "Đã hoàn thành",
    markComplete: "Đánh dấu hoàn thành",
    expand: "Mở rộng",
    collapse: "Thu gọn",
    expandAll: "Mở tất cả",
    collapseAll: "Thu gọn tất cả",
    reset: "Đặt lại",
    moveSidebarLeft: "Chuyển thanh bên sang trái",
    moveSidebarRight: "Chuyển thanh bên sang phải",
    collapseSidebar: "Thu gọn thanh bên",
    showSidebar: "Hiện thanh bên",
    byAuthor: "bởi {{name}}",
    learnersHere_other: "{{count}} người học đang ở đây",
    peersCompleted_other: "{{count}} người học khác đã hoàn thành",
    daysAgoShort: "{{count}} ngày trước",
    hoursAgoShort: "{{count}} giờ trước",
    minutesAgoShort: "{{count}} phút trước",
    questionsPracticed: "câu hỏi",
    vocabulariesReviewed: "từ vựng",
    studied: "đã học",
    /** `/session/:slug/practice` — vùng luyện tập. */
    breadcrumbPractice: "Luyện tập",
    toggleSpeakingGuide: "Bật/tắt hướng dẫn nói (⌘B)",
    resetProgress: "Đặt lại tiến độ",
    toggleAiChat: "Bật/tắt trò chuyện AI (⌘/)",
    aiAssistant: "Trợ lý AI",
    /** Dải phím tắt. Ký tự giữ nguyên, chỉ phần chữ được dịch. */
    navHintOr: "hoặc",
    navHintNavigate: "để chuyển",
    navHintGuide: "hướng dẫn",
    navHintChat: "trò chuyện",
    /** `StudyGuideSection` (bảng đầy đủ) và `PracticeStudyGuide` (thanh bên). */
    guideStepByStepApproach: "Cách thực hiện từng bước",
    guideExampleAnswer: "Câu trả lời mẫu",
    guideKeyPointsToCover: "Các ý chính cần đề cập",
    guideStepByStep: "Từng bước",
    guideKeyPoints: "Các ý chính",
    guideTab: "Hướng dẫn",
    vocabTab: "Từ vựng",
    speakingTab: "Nói",
    studyPanelView: "Chế độ xem bảng học",
    markAsComplete: "Đánh dấu hoàn thành",
    markAsIncomplete: "Bỏ đánh dấu hoàn thành",
    guideUnavailable: "Chưa có hướng dẫn học cho câu hỏi này",
    guideUnavailableYet: "Chưa có hướng dẫn học cho câu hỏi này",
    /** Cột trò chuyện luyện tập + trạng thái rỗng của nó. */
    currentQuestion: "Câu hỏi hiện tại:",
    /** `ImmersiveQuestionView` — các chip trên câu hỏi. */
    questionPosition: "Câu hỏi {{current}} / {{total}}",
    practicedBadge: "Đã luyện",
    /** `PracticeDictionary` — widget từ điển cố định. */
    openDictionary: "Mở từ điển",
    dictionaryLabel: "Từ điển",
    /** `LargeRecordingInterface` — mọi trạng thái của máy ghi âm. */
    recording: {
      analyzingAnswer: "Đang phân tích câu trả lời của bạn…",
      analyzingHint: "Lydi đang lắng nghe và chấm điểm phần nói của bạn.",
      secureTitle: "Cần kết nối an toàn để ghi âm",
      secureDescription: "Chỉ có thể truy cập micrô qua HTTPS (hoặc trên localhost). Hãy tải lại trang bằng một địa chỉ an toàn để ghi âm câu trả lời.",
      unsupportedTitle: "Trình duyệt này không hỗ trợ ghi âm",
      unsupportedDescription: "Trình duyệt của bạn không thể truy cập micrô. Vui lòng thử phiên bản mới của Chrome, Firefox, Safari hoặc Edge.",
      micUnreachableTitle: "Không thể kết nối micrô",
      micUnreachableHint: "Hãy kiểm tra xem trang này đã được phép dùng micrô trong phần quyền của thanh địa chỉ trình duyệt, rồi thử lại.",
      getReady: "Chuẩn bị nói thôi...",
      accessingMicrophone: "Đang truy cập micrô...",
      pausedHint: "Đã tạm dừng ghi âm — nhấn nút phát để tiếp tục",
      recLabel: "Đang ghi âm",
      recordAgain: "Ghi lại",
      /** Viết hoa khác `recordAgain` — nút xác nhận trong hộp thoại. */
      recordAgainConfirm: "Ghi lại",
      recordAgainTitle: "Ghi lại?",
      recordAgainDescription: "Thao tác này sẽ bắt đầu một bản ghi mới và thay thế câu trả lời hiện tại cùng nhận xét của nó.",
      keepCurrent: "Giữ bản hiện tại",
      analyzingShort: "Đang phân tích...",
      viewAiFeedback: "Xem nhận xét AI",
      getAiFeedback: "Nhận nhận xét AI",
      tapToStartRecording: "Chạm để bắt đầu ghi âm",
      speakClearly: "Nói rõ ràng và đừng vội",
      liveTranscription: "Chép lời trực tiếp",
      liveTranscriptionUnavailable: "Chưa thể chép lời trực tiếp — chúng tôi sẽ chép lại khi bạn nói xong.",
      liveListening: "Đang nghe...",
    },
    /** `lib/seo.ts` — đọc `i18next` trực tiếp vì không phải component. */
    seo: {
      titleSuffix: "Buổi luyện nói",
      practiceTitlePrefix: "Luyện tập",
      description: "Luyện nói về \"{{topic}}\"{{categoryText}}. Bao gồm {{questionCount}} {{questionNoun}} và {{vocabularyCount}} {{vocabularyNoun}} kèm định nghĩa và ví dụ. Rất phù hợp với người học ngôn ngữ muốn cải thiện kỹ năng hội thoại.",
      categoryIn: " thuộc mục {{category}}",
      questionNoun_other: "câu hỏi luyện tập",
      vocabularyNoun_other: "từ vựng chính",
      resourceType: "Buổi luyện nói",
      educationalLevel: "Từ sơ cấp đến nâng cao",
      keywordSpeakingPractice: "luyện nói",
      keywordLanguageLearning: "học ngôn ngữ",
      keywordConversationPractice: "luyện hội thoại",
      keywordEnglishSpeaking: "luyện nói tiếng Anh",
      keywordAiTutor: "gia sư AI",
    },
    savedTitle: "Đã lưu!",
    savedDescription: "Từ và ghi chú của bạn được lưu vào thư viện — ôn tập bất cứ lúc nào.",
    viewSavedItems: "Xem mục đã lưu",
    streakProtected: "Chuỗi ngày của bạn đã được bảo vệ bằng lượt đóng băng!",
  },
  /**
   * Widget trợ lý AI, dùng chung cho cột trò chuyện luyện tập và trợ lý nổi.
   * Bốn chip gợi ý cố tình KHÔNG nằm ở đây — chúng là đầu vào gửi cho mô hình
   * nên giữ nguyên tiếng Anh (xem `ChatMessages`).
   */
  aiAssistant: {
    newConversation: "Cuộc trò chuyện mới",
    practiceCoach: "Huấn luyện viên luyện tập",
    learningCoach: "Huấn luyện viên học tập",
    subtitlePractice: "Hỏi gợi ý, giải thích, từ vựng hoặc câu trả lời mẫu.",
    subtitleGeneric: "Người bạn đồng hành học ngôn ngữ của riêng bạn",
    viewMode: "Kiểu hiển thị",
    floating: "Nổi",
    sidebar: "Thanh bên",
    minimize: "Thu nhỏ",
    editingPlaceholder: "Đang sửa tin nhắn... (Nhấn Escape để hủy)",
    inputPlaceholder: "Hỏi huấn luyện viên của bạn...",
    stopGeneration: "Dừng tạo câu trả lời",
    signInTitle: "Đăng nhập để trò chuyện với Lyder",
    signInBody: "Trợ lý học tập lưu lại lịch sử và trả lời dựa trên trang bạn đang xem. Điều đó cần một tài khoản.",
  },
  dialogs: {
    close: "Đóng",
    clearSearch: "Xóa tìm kiếm",
    reviewQueue: "Hàng đợi ôn tập",
    testMicrophone: "Thử micrô",
    selectMicrophone: "Chọn micrô",
    selectSpeaker: "Chọn loa",
    quickPresets: "Tuỳ chọn nhanh",
    minutesPlaceholder: "Phút",
    shareSession: "Chia sẻ phiên",
    shareLink: "Liên kết chia sẻ",
    share: "Chia sẻ",
    shareTo: "Chia sẻ tới",
    practiceSession: "Buổi luyện tập",
    linkCopied: "Đã sao chép liên kết",
    linkCopiedHint: "Bất kỳ ai có liên kết đều có thể mở phiên này.",
    shareImage: "Ảnh chia sẻ",
    shareImageHint: "Ảnh dọc 4:5 · chủ đề, số câu và mã QR",
    shareImagePreview: "Xem trước ảnh sẽ được chia sẻ",
    copyImage: "Sao chép ảnh",
    imageCopied: "Đã sao chép ảnh chia sẻ",
    imageCopiedHint: "Dán vào cuộc trò chuyện hoặc bài đăng.",
    downloadQr: "Tải xuống",
    imageDownloaded: "Đã tải ảnh",
    imageDownloadedHint: "Sẵn sàng chia sẻ lên Zalo hoặc Facebook.",
    posterQuestions: "{{count}} câu hỏi",
    posterWords: "{{count}} từ",
    visibility: "Quyền riêng tư",
    maxMembers: "Số thành viên tối đa",
    numberPlaceholder: "Số",
    enterGroupName: "Nhập tên nhóm",
    addDescription: "Thêm mô tả...",
    inviteToGroup: "Mời vào nhóm",
    inviteLinkCopied: "Đã sao chép liên kết mời!",
    inviteLinkCopyFailed: "Sao chép liên kết thất bại",
    invitePublicLinkHint: "Gõ để tìm người dùng, hoặc nhập email thủ công. Để trống để tạo liên kết mời công khai.",
    copied: "Đã sao chép!",
    copyLink: "Sao chép liên kết",
    addingUser: "Đang thêm...",
    addUser: "Thêm người dùng",
    createInvitation: "Tạo lời mời",
    creatingInvitation: "Đang tạo...",
    memberOptions: "Tùy chọn thành viên",
    joinedAgo: "Đã tham gia {{time}}",
    searchUserOrEmail: "Tìm người dùng hoặc nhập email",
    searchUserPlaceholder: "Gõ để tìm người dùng hoặc nhập email...",
    inviteLink: "Liên kết mời",
    searchTopicPlaceholder: "Tìm theo chủ đề hoặc chuyên mục...",
    setPracticePlan: "Đặt kế hoạch luyện tập",
    loadingTopics: "Đang tải chủ đề công khai...",
    loadingSessions: "Đang tải phiên...",
    chooseTopicsDescription: "Chọn chủ đề để chia sẻ với nhóm của bạn",
    shareTopic: "Chia sẻ chủ đề",
    shareToGroup: "Chia sẻ với nhóm học",
    noGroupsFound: "Không tìm thấy nhóm",
    pasteUrl: "Dán liên kết YouTube (ví dụ: https://youtube.com/watch?v=...)",
    choosePracticeMode: "Chọn chế độ luyện tập",
    comingSoon: "Sắp có",
    dictation: "Chép lời",
    shadowing: "Nhại theo",
    noVideosYet: "Chưa có video nào",
    createCollection: "Tạo bộ sưu tập",
    collectionTitle: "Tên bộ sưu tập",
    collectionDescription: "Mô tả bộ sưu tập",
    noMatches: "Không có kết quả",
    loadingCollections: "Đang tải bộ sưu tập...",
    typeWordPlaceholder: "Nhập một từ và nhấn Enter",
    askCoach: "Hỏi trợ lý",
    define: "Định nghĩa",
    notes: "Ghi chú",
    listen: "Nghe",
    stop: "Dừng",
    save: "Lưu",
    saved: "Đã lưu",
    displayNamePlaceholder: "Người khác nên gọi bạn là gì?",
    bioPlaceholder: "Nói thêm về bản thân (không bắt buộc)",
    calendar: "Lịch",
    challenges: "Thử thách",
    milestones: "Cột mốc",
    freezes: "Đóng băng",
    dialogTabs: "Tab của hộp thoại",
    createGroup: "Tạo nhóm học",
    editGroup: "Sửa nhóm học",
    createGroupDescription: "Tạo nhóm học mới để cùng học với người khác",
    editGroupDescription: "Cập nhật cài đặt và thông tin của nhóm học",
    groupName: "Tên nhóm",
    groupNameRequired: "Vui lòng nhập tên nhóm",
    groupNameTooShort: "Tên nhóm phải có ít nhất 3 ký tự",
    groupNameTooLong: "Tên nhóm không được vượt quá 100 ký tự",
    charactersCount: "{{count}}/{{max}} ký tự",
    settings: "Cài đặt",
    public: "Công khai",
    private: "Riêng tư",
    currentMin: "Hiện tại: {{count}}. Tối thiểu {{count}}",
    maxRange: "1–100 tối đa",
    cancel: "Hủy",
    creating: "Đang tạo...",
    updating: "Đang cập nhật...",
    updateGroup: "Cập nhật nhóm",
    newGroup: "Nhóm mới",
    emoji: "Biểu tượng cảm xúc",
    upload: "Tải lên",
    changeEmoji: "Đổi biểu tượng",
    chooseEmoji: "Chọn biểu tượng",
    uploading: "Đang tải lên...",
  },
  review: {
    dailyReview: "Ôn tập hằng ngày",
    wordsReady: "Từ đã đến hạn",
    videosToRevisit: "Video nên xem lại",
    timeToReview: "Đến lúc ôn tập",
    dueNow: "Đến hạn",
    words: "Từ",
    videos: "Video",
    estimate: "Ước tính",
    dueToday: "Đến hạn hôm nay",
    notPracticed: "Chưa luyện tập",
    today: "Hôm nay",
    yesterday: "Hôm qua",
    nothingWaiting: "Hiện không có gì đang chờ bạn.",
    moreWaiting: "+{{count}} từ đang chờ",
    notNow: "Để sau — nhắc tôi sau hôm nay",
    reviewWords: "Ôn {{count}} từ",
    practiceVideos: "Luyện {{count}} video",
    listSeparator: " và ",
    queueSummary: "{{items}} đã sẵn sàng. Khoảng {{minutes}} phút luyện tập.",
    daysAgo_other: "{{count}} ngày trước",
    weekAgo: "1 tuần trước",
    weeksAgo_other: "{{count}} tuần trước",
    monthAgo: "1 tháng trước",
    monthsAgo_other: "{{count}} tháng trước",
    daysLate_other: "trễ {{count}} ngày",
  },
  push: {
    unavailable: "Thông báo đẩy không khả dụng",
    blocked: "Thông báo đẩy đã bị chặn",
    enable: "Bật thông báo đẩy",
    unblockHint: "Vui lòng mở lại trong cài đặt trình duyệt",
    notified: "Nhận thông báo khi phiên được chia sẻ",
    retrying: "Đang thử lại...",
    retry: "Thử lại",
    refresh: "Làm mới",
    enabling: "Đang bật...",
    enableButton: "Bật",
  },
  /** Bảng "Thống kê" trong hộp thoại hồ sơ. */
  settings: {
    statisticsSubtitle: "Hoạt động của bạn trên nền tảng",
    noStatistics: "Chưa có thống kê",
    noStatisticsHint: "Bắt đầu luyện tập để xem tiến độ của bạn",
    totalLearningTime: "Thời gian học",
    topicsStudied_one: "Đã học {{count}} chủ đề",
    topicsStudied_other: "Đã học {{count}} chủ đề",
    totalSessions: "Số buổi",
    publicSessions: "Công khai",
    studyGroupsShort: "Nhóm",
    memberSince: "Thành viên từ",
    lastActivity: "Hoạt động gần nhất",
    streakRecord: "Kỷ lục chuỗi",
    currentShort: "Hiện tại",
    bestShort: "Tốt nhất",
  },
  rollAndTalk: {
    title: "Quay & Nói",
    eyebrow: "Lotería",
    subtitle: "Rút một thẻ, trả lời thành tiếng và lấp đầy một hàng trên tabla của bạn.",
    speakFor: "Nói trong",
    speakForSeconds: "Nói trong {{seconds}}",
    speakDurationPresets: "Các mốc thời gian nói",
    speakDurationCustom: "Thời gian nói tính bằng giây",
    studyGuide: "Hướng dẫn học",
    drawCard: "Rút một thẻ",
    drawShort: "Rút thẻ",
    draw: "rút thẻ",
    drawing: "Đang rút…",
    drawingStatus: "Đang rút một thẻ",
    callingCard: "Đang gọi một thẻ",
    nowCalling: "Đang gọi",
    roundComplete: "Hết ván",
    calledStatus: "Đã gọi {{name}}",
    cardNumber: "Số {{number}}",
    yourTabla: "Tabla của bạn",
    tablaProgress: "Đã gọi {{called}} / {{total}}",
    tablaSpoken: "{{spoken}} đã trả lời",
    tablaHint:
      "Lấp đầy một hàng, một cột hoặc đường chéo để hô ¡Lotería! Nhấn vào thẻ đã gọi để nghe lại.",
    called: "đã gọi",
    spoken: "đã trả lời",
    notCalled: "chưa gọi",
    mute: "Tắt âm bàn chơi",
    unmute: "Bật âm bàn chơi",
    emptyTitle: "Không có thẻ để chia",
    emptyLevel: "Chưa có câu hỏi công khai ở cấp độ {{level}}.",
    emptyBody: "Chưa có câu hỏi công khai nào.",
    emptyHint:
      "Thử cấp độ khác, hoặc duyệt thư viện chủ đề và luyện tập một phiên đầy đủ.",
    clearFilter: "Xóa bộ lọc",
    browseTopics: "Duyệt chủ đề",
    openInPractice: "Mở trong luyện tập",
    helperDeepLink: "Mở màn hình ghi âm cho đúng câu hỏi này.",
    helperNoSession: "Câu hỏi mẫu tích hợp không có phiên để ghi âm.",
    hear: "nghe",
    record: "ghi âm",
    anyLevel: "Mọi cấp độ",
    filterByLevel: "Lọc câu hỏi theo cấp độ",
    win: {
      title: "¡Lotería!",
      topRow: "hàng trên",
      middleRow: "hàng giữa",
      bottomRow: "hàng dưới",
      leftColumn: "cột trái",
      middleColumn: "cột giữa",
      rightColumn: "cột phải",
      diagonal: "đường chéo",
      diagonalReverse: "đường chéo còn lại",
      fullTabla: "cả tabla",
      body: "Bạn đã lấp đầy {{line}} — {{spoken}} / {{total}} thẻ đã trả lời thành tiếng.",
      newTabla: "Chia tabla mới",
    },
    level: {
      beginner: "Sơ cấp",
      intermediate: "Trung cấp",
      advanced: "Nâng cao",
    },
  },
  onboarding: {
    /** `TourTooltip` — khung của một bước, không phải nội dung của bước. */
    tooltip: {
      closeTour: "Đóng hướng dẫn",
      step: "Bước hướng dẫn",
      goToStep: "Đến bước {{step}}",
      previousStep: "Bước trước",
      nextStep: "Bước tiếp theo",
      finishTour: "Kết thúc hướng dẫn",
      remindLater: "Nhắc sau",
      gotIt: "Đã hiểu",
    },
    /** `TourHelpButton` — lối vào để xem lại hướng dẫn. */
    help: {
      showMeAround: "Chỉ tôi xem quanh",
    },
    tours: {
      home: {
        createTopic: {
          title: "Tạo chủ đề luyện tập",
          content: "Nhấn vào đây để tạo chủ đề luyện tập của riêng bạn. Nhập bất kỳ chủ đề nào và AI sẽ tạo câu hỏi cùng từ vựng để bạn luyện tập.",
        },
        vocabularyWidget: {
          title: "Từ vựng",
          content: "Nhấn vào đây để mở các từ đã lưu. Ôn tập lặp lại ngắt quãng mỗi ngày để đưa từ vào trí nhớ dài hạn.",
        },
        collectionsWidget: {
          title: "Bộ sưu tập",
          content: "Mở các bộ sưu tập từ vựng — những bộ từ theo chủ đề do bạn hoặc cộng đồng tạo. Duyệt, lưu hoặc tự tạo bộ của riêng bạn để học sâu một chủ đề.",
        },
        practiceWidget: {
          title: "Luyện tập",
          content: "Nhấn vào đây để quay lại chủ đề bạn vừa luyện tập, hoặc duyệt để chọn chủ đề mới. Hãy luyện tập ít nhất một phiên mỗi ngày để giữ chuỗi ngày.",
        },
        streakWidget: {
          title: "Chuỗi ngày & cấp độ",
          content: "Nhấn vào đây để xem chuỗi ngày và tiến độ XP của bạn. Luyện tập hằng ngày để giữ chuỗi ngày và thăng cấp.",
        },
        leaderboardWidget: {
          title: "Bảng xếp hạng",
          content: "Nhấn vào đây để xem bảng xếp hạng toàn cầu. Theo dõi thứ hạng của bạn theo thời gian học, XP, chuỗi ngày và số lượt ôn từ vựng.",
        },
        videosWidget: {
          title: "Luyện tập qua video",
          content: "Nhấn vào đây để duyệt các bài học video. Chọn một video để luyện nhại theo người nói hoặc gõ lại những gì bạn nghe (chép lời).",
        },
        aiWidget: {
          title: "Tạo chủ đề bằng AI",
          content: "Nhấn vào đây để trò chuyện với AI và tạo chủ đề luyện tập mới theo kiểu hội thoại. Mô tả bạn muốn luyện tập gì và AI sẽ gợi ý chủ đề kèm câu hỏi và từ vựng.",
        },
        studyGroupsWidget: {
          title: "Nhóm học của bạn",
          content: "Các nhóm học bạn đã tham gia nằm ở đây trong phần truy cập nhanh. Nhấn vào một nhóm để xem thành viên, chủ đề được chia sẻ và bảng xếp hạng của nhóm. Dùng 'Xem tất cả' để duyệt hoặc tạo thêm.",
        },
        featuredCollections: {
          title: "Bộ sưu tập",
          content: "Duyệt các bộ sưu tập từ vựng tại đây — những bộ từ theo chủ đề được chọn lọc cho các chủ đề phổ biến. Nhấn 'Xem tất cả' để xem toàn bộ thư viện hoặc tạo bộ của riêng bạn.",
        },
        topicOfDay: {
          title: "Chủ đề trong ngày",
          content: "Mỗi ngày chúng tôi chọn một chủ đề nổi bật. Nhấn vào thẻ để xem câu hỏi và từ vựng, hoặc nhấn nút luyện tập để bắt đầu phiên nói ngay lập tức.",
        },
        groupTopicOfDay: {
          title: "Chủ đề trong ngày của nhóm",
          content: "Nếu bạn đang ở trong một nhóm học, đây là chủ đề hiện tại của nhóm. Luyện tập chủ đề đó để góp điểm cho bảng xếp hạng của nhóm.",
        },
        allSet: {
          title: "Bạn đã sẵn sàng",
          content: "Chọn một chủ đề, luyện nói và xây dựng chuỗi ngày mỗi ngày. Dùng mục 'Xem hướng dẫn' trong menu avatar để xem lại phần này bất cứ lúc nào.",
        },
      },
      myTopics: {
        quickGenerate: {
          title: "Tạo nhanh",
          content: "Nhấn 'Tạo nhanh' để nhập tên chủ đề, chọn chuyên mục và ngay lập tức có một bộ luyện tập đầy đủ gồm câu hỏi, từ vựng và hướng dẫn nói (tuỳ chọn).",
        },
        chatWithAi: {
          title: "Trò chuyện với AI",
          content: "Nhấn vào đây để cùng AI nảy ý tưởng chủ đề theo kiểu hội thoại. Mô tả bạn muốn luyện tập gì và AI sẽ gợi ý chủ đề kèm câu hỏi và từ vựng.",
        },
        sidebarHistory: {
          title: "Lịch sử chủ đề",
          content: "Mọi chủ đề bạn đã tạo hoặc luyện tập đều xuất hiện ở đây theo thứ tự thời gian. Nhấn vào một chủ đề để xem câu hỏi và từ vựng, hoặc tiếp tục luyện tập. Dùng 'Thêm' để tải các chủ đề cũ hơn.",
        },
        recentTopics: {
          title: "Chủ đề gần đây của bạn",
          content: "Mọi chủ đề bạn tạo đều xuất hiện ở đây. Nhấn vào một chủ đề để xem câu hỏi và từ vựng, hoặc bắt đầu một phiên luyện tập. Dùng biểu tượng ghim để giữ các chủ đề quan trọng ở trên cùng.",
        },
      },
      myTopicsAi: {
        aiChat: {
          title: "Tạo chủ đề bằng AI",
          content: "Trò chuyện với AI tại đây để nảy ý tưởng và tạo chủ đề luyện tập. Mô tả bạn muốn luyện tập gì và AI sẽ gợi ý chủ đề kèm câu hỏi và từ vựng.",
        },
        messageInput: {
          title: "Nhập tin nhắn của bạn",
          content: "Nhập một chủ đề bạn muốn luyện tập tại đây — ví dụ 'gọi cà phê' hay 'cụm từ du lịch'. Nhấn Enter để gửi, Shift+Enter để xuống dòng mới.",
        },
        send: {
          title: "Gửi tin nhắn",
          content: "Nhấn nút gửi hoặc nhấn Enter để nhờ AI tạo một chủ đề luyện tập. Khi sẵn sàng, bản xem trước sẽ xuất hiện ở bên phải.",
        },
        previewPanel: {
          title: "Xem lại và lưu",
          content: "Khi AI tạo bản nháp chủ đề, nó sẽ xuất hiện ở đây. Xem lại câu hỏi và từ vựng, rồi nhấn 'Tạo chủ đề' để lưu vào danh sách chủ đề của bạn.",
        },
      },
      myTopicsAiDetail: {
        aiChat: {
          title: "Tiếp tục cuộc trò chuyện",
          content: "Đây là cuộc trò chuyện AI đang diễn ra của bạn. Cứ tiếp tục trò chuyện để tinh chỉnh chủ đề, yêu cầu điều chỉnh hoặc tạo thêm tài liệu luyện tập.",
        },
        messageInput: {
          title: "Gửi tin nhắn tiếp theo",
          content: "Nhập một tin nhắn tiếp theo để điều chỉnh chủ đề — nhờ câu hỏi khó hơn, từ vựng khác, hay một góc nhìn mới về cùng chủ đề.",
        },
        previewPanel: {
          title: "Xem các bản nháp",
          content: "Các bản nháp chủ đề từ cuộc trò chuyện này xuất hiện ở đây. Nhấn 'Tạo chủ đề' để lưu một bản nháp, hoặc dùng 'Xem' / 'Sửa' để mở một chủ đề bạn đã lưu.",
        },
      },
      myTopicDetail: {
        practiceTopic: {
          title: "Bắt đầu luyện tập",
          content: "Nhấn 'Xem' để mở phiên luyện tập của chủ đề này. Bạn sẽ có thể ghi âm câu trả lời của mình và nhận phản hồi tức thì về độ trôi chảy và cách phát âm.",
        },
        regenerateTopic: {
          title: "Tạo lại nội dung",
          content: "Nhấn 'Tạo lại' để làm mới câu hỏi, từ vựng và hướng dẫn nói của chủ đề này. Hữu ích khi bạn muốn thêm tài liệu luyện tập hoặc thấy nội dung quá dễ/quá khó.",
        },
        pinTopic: {
          title: "Ghim để truy cập nhanh",
          content: "Nhấn biểu tượng ghim để giữ chủ đề này ở đầu danh sách bên cạnh. Chủ đề đã ghim luôn dễ tìm lại khi bạn muốn luyện tập.",
        },
        shareTopic: {
          title: "Chia sẻ với người khác",
          content: "Nhấn 'Sao chép liên kết' để chia sẻ chủ đề này với bạn bè hoặc bạn cùng lớp. Hãy để chủ đề ở chế độ công khai để bất kỳ ai cũng có thể tìm và luyện tập.",
        },
        shareToGroup: {
          title: "Chia sẻ với nhóm học",
          content: "Nhấn 'Chia sẻ với nhóm' để chia sẻ chủ đề này với một nhóm học bạn tham gia. Thành viên có thể cùng luyện tập và kiếm điểm trên bảng xếp hạng của nhóm.",
        },
        topicBanner: {
          title: "Cá nhân hoá bằng ảnh bìa",
          content: "Nhấn vào đây để thêm hoặc đổi ảnh bìa cho chủ đề. Bạn có thể tải ảnh của riêng mình hoặc tìm trên Unsplash để chủ đề nổi bật hơn.",
        },
        detailTabs: {
          title: "Duyệt các tab nội dung",
          content: "Chuyển qua lại giữa Câu hỏi, Từ vựng và Hướng dẫn nói bằng các tab này. Mỗi tab hiển thị phần nội dung AI đã tạo cho chủ đề này.",
        },
        detailContent: {
          title: "Nội dung luyện tập",
          content: "Khu vực nội dung chính hiển thị câu hỏi, từ vựng hoặc mẹo nói tuỳ vào tab nào đang hoạt động. Nhấn vào bất kỳ mục nào để luyện tập hoặc ôn lại.",
        },
      },
      topics: {
        createTopic: {
          title: "Tạo một chủ đề",
          content: "Nhấn 'Tạo chủ đề' để tạo chủ đề luyện tập của riêng bạn. Nhập bất kỳ chủ đề nào và AI sẽ tạo câu hỏi cùng từ vựng để bạn luyện tập.",
        },
        searchTopics: {
          title: "Tìm chủ đề",
          content: "Nhập tại đây để tìm chủ đề theo tên. Kết quả cập nhật ngay khi bạn gõ. Nhấn chữ X để xóa tìm kiếm.",
        },
        filterTabs: {
          title: "Lọc theo chuyên mục",
          content: "Dùng các tab này để chuyển giữa Tất cả, Gần đây, Thịnh hành, Phổ biến và Được lưu nhiều nhất. Thanh chuyên mục bên trái giúp bạn lọc sâu hơn.",
        },
        topicsGrid: {
          title: "Chọn một chủ đề để luyện tập",
          content: "Nhấn vào thẻ chủ đề bất kỳ để xem câu hỏi và từ vựng. Dùng nút luyện tập trên mỗi thẻ để vào thẳng một phiên nói.",
        },
      },
      vocabulary: {
        vocabTabs: {
          title: "Chuyển giữa các chế độ xem",
          content: "Dùng các tab này để chuyển giữa 'Từ của tôi' và 'Bộ sưu tập'. Bộ sưu tập giúp bạn gom từ thành các bộ theo chủ đề để học tập trung.",
        },
        addWord: {
          title: "Thêm từ mới",
          content: "Nhấn 'Thêm từ' để tạo từ vựng mới bằng AI. Bạn có thể nhập một từ hoặc một chủ đề, và AI sẽ tạo định nghĩa, ví dụ cùng bản dịch.",
        },
        dailyReview: {
          title: "Nhắc ôn tập hằng ngày",
          content: "Khi có từ đến hạn, thẻ màu hổ phách này sẽ nhắc bạn ôn chúng. Con số lớn là số từ đang chờ, và mỗi thanh bên dưới là tỉ lệ của một chủ đề trong hàng đợi — nhấn vào một dòng, hoặc 'Ôn tất cả', để bắt đầu.",
        },
        searchWords: {
          title: "Tìm trong từ của bạn",
          content: "Nhập tại đây để tìm trong các từ đã lưu theo tên hoặc định nghĩa. Kết quả cập nhật ngay khi bạn gõ.",
        },
        filterPills: {
          title: "Lọc theo trạng thái ôn tập",
          content: "Dùng các viên này để lọc bộ thẻ theo Tất cả, Đến hạn (từ sẵn sàng ôn), Đã ôn, Chưa ôn hoặc Thiếu thông tin. Mỗi viên hiển thị số lượng của trạng thái đó.",
        },
        stats: {
          title: "Số liệu học tập của bạn",
          content: "Theo dõi tiến độ từ vựng tại đây: tổng số đã lưu, số đến hạn ôn tập, số đã ôn hôm nay và số từ còn thiếu thông tin.",
        },
        learningPath: {
          title: "Lộ trình học",
          content: "Xem cách bạn nắm từ vựng qua bốn cấp: Mới, Đang học, Đang ôn và Đã ghi nhớ. Nhấn vào một cấp để lọc bộ thẻ theo cấp độ đó.",
        },
      },
      vocabularyNotes: {
        notesNew: {
          title: "Tạo một ghi chú",
          content: "Nhấn 'Ghi chú mới' để ghi lại một ghi chú cá nhân tự do — ý tưởng, nhắc nhở, hoặc bất cứ điều gì bạn muốn nhớ. Ghi chú mở trong trình soạn thảo văn bản.",
        },
        vocabTabs: {
          title: "Chuyển giữa các chế độ xem",
          content: "Dùng các tab này để chuyển giữa 'Từ của tôi', 'Bộ sưu tập' và 'Ghi chú'. Bạn đang ở tab Ghi chú — ghi chú từ và ghi chú cá nhân của bạn nằm ở đây.",
        },
        notesSearch: {
          title: "Tìm trong ghi chú của bạn",
          content: "Nhập tại đây để tìm trong cả ghi chú từ lẫn ghi chú cá nhân theo từ khóa. Kết quả cập nhật ngay khi bạn gõ.",
        },
        notesFilters: {
          title: "Lọc theo loại",
          content: "Dùng các viên này để hiện Tất cả ghi chú, chỉ 'Ghi chú từ' (gắn với từ vựng bạn đã học), hoặc chỉ ghi chú 'Cá nhân'. Mỗi viên hiển thị số lượng của nó.",
        },
        notesGrid: {
          title: "Ghi chú của bạn",
          content: "Tất cả ghi chú của bạn xuất hiện ở đây dưới dạng thẻ, mới nhất trước. Nhấn vào thẻ bất kỳ để sửa tại chỗ, hoặc dùng menu để xóa.",
        },
      },
      vocabularyCollections: {
        createCollection: {
          title: "Tạo một bộ sưu tập",
          content: "Nhấn 'Tạo bộ sưu tập' để dựng một bộ từ theo chủ đề mới. Thêm từ thủ công hoặc để AI tạo giúp, rồi theo dõi mức độ thành thạo khi bạn ôn tập.",
        },
        stats: {
          title: "Số liệu của bạn trong nháy mắt",
          content: "Xem các bộ của cộng đồng, số từ hiện có, bộ sưu tập của bạn và số đang học dở. Dùng mục này để theo dõi lượng từ vựng bạn đang xây dựng trên tất cả các bộ sưu tập.",
        },
        search: {
          title: "Tìm bộ sưu tập",
          content: "Nhập tại đây để tìm bộ sưu tập theo tên hoặc mô tả. Kết quả cập nhật khi bạn gõ.",
        },
        filterPills: {
          title: "Lọc bộ sưu tập",
          content: "Dùng các viên này để lọc bộ sưu tập theo Tất cả, Của tôi, Công khai, Chia sẻ với tôi hoặc Từ chủ đề. Mỗi viên hiển thị số lượng của nhóm đó.",
        },
      },
      vocabularyCreateCollection: {
        collectionTitle: {
          title: "Đặt tên cho bộ sưu tập",
          content: "Đặt cho bộ sưu tập một tên rõ ràng — như 'Tiếng Anh kinh doanh' hay 'Ôn thi IELTS'. Đây là cách bạn sẽ tìm thấy nó sau này trong danh sách bộ sưu tập.",
        },
        collectionVisibility: {
          title: "Đặt quyền hiển thị",
          content: "Bộ sưu tập công khai sẽ xuất hiện trong thư mục cộng đồng. Bộ sưu tập riêng tư chỉ bạn mới thấy. Bạn có thể đổi bất cứ lúc nào.",
        },
        collectionWordSource: {
          title: "Chọn nguồn từ của bạn",
          content: "Chọn từ trong từ vựng đã lưu, hoặc nhập từ mới để tạo định nghĩa bằng AI. Bạn có thể kết hợp cả hai nguồn trong cùng một bộ sưu tập.",
        },
        collectionWordSearch: {
          title: "Tìm và chọn từ",
          content: "Tìm trong từ vựng đã lưu theo từ hoặc định nghĩa. Lọc theo trình độ hoặc loại từ, rồi tick những từ bạn muốn đưa vào bộ sưu tập này.",
        },
        collectionInlineInput: {
          title: "Thêm từ mới",
          content: "Nhập bất kỳ từ nào và nhấn Enter để xếp vào hàng chờ. Sau đó nhấn 'Tạo' để tạo định nghĩa, ví dụ và cách phát âm IPA — tất cả nhờ AI.",
        },
        collectionInlineArea: {
          title: "Tạo định nghĩa",
          content: "Nhấn 'Tạo' để tạo định nghĩa, ví dụ và IPA cho các từ đang chờ. Xem lại kết quả, tick những từ bạn muốn và thêm chúng vào bộ sưu tập.",
        },
        collectionYourDeck: {
          title: "Bộ thẻ của bạn",
          content: "Thanh bên này hiển thị mọi từ trong bộ sưu tập dưới dạng thẻ. Bạn có thể gỡ từ từ đây. Số lượng cập nhật trực tiếp khi bạn thêm hoặc gỡ từ từ cả hai nguồn.",
        },
        collectionAction: {
          title: "Xem lại và lưu",
          content: "Sau khi đã thêm từ, nhấn 'Xem lại' để kiểm tra lựa chọn của bạn. Rồi nhấn 'Tạo bộ sưu tập' để lưu — nó sẽ xuất hiện trên trang bộ sưu tập của bạn.",
        },
      },
      vocabularyCollectionDetail: {
        detailPractice: {
          title: "Bắt đầu luyện tập",
          content: "Nhấn 'Luyện tập' để bắt đầu ôn các từ của bộ sưu tập này ở chế độ thẻ ghi nhớ và trắc nghiệm. Tiến độ và mức độ thành thạo sẽ cập nhật khi bạn học.",
        },
        detailAddWords: {
          title: "Thêm từ",
          content: "Nhấn 'Thêm từ' để bổ sung thêm từ vựng vào bộ sưu tập này. Chọn từ trong các từ đã lưu hoặc để AI tạo từ mới.",
        },
        detailShare: {
          title: "Chia sẻ bộ sưu tập này",
          content: "Nhấn 'Chia sẻ' để sao chép liên kết tới bộ sưu tập này, giúp người khác tìm và nhập về. Chia sẻ lên mạng xã hội hoặc gửi trực tiếp cho bạn bè.",
        },
        detailShareGroup: {
          title: "Chia sẻ với nhóm học",
          content: "Nhấn 'Chia sẻ với nhóm' để chia sẻ bộ sưu tập này với thành viên của một nhóm học của bạn. Mọi người trong nhóm đều có thể nhập về và luyện tập.",
        },
        detailEdit: {
          title: "Sửa bộ sưu tập",
          content: "Nhấn 'Sửa' để đổi tên, mô tả, quyền hiển thị, hoặc sắp xếp lại các từ trong bộ sưu tập này.",
        },
        detailDelete: {
          title: "Xóa bộ sưu tập",
          content: "Nhấn 'Xóa' để xóa vĩnh viễn bộ sưu tập này. Thao tác này không thể hoàn tác — mọi từ sẽ bị gỡ khỏi bộ sưu tập.",
        },
        detailStats: {
          title: "Tiến độ của bạn trong nháy mắt",
          content: "Xem tổng số từ, số từ bạn đã ôn, tỉ lệ thành thạo và số từ đã lưu vào thư viện của bạn. Thanh thành thạo đầy dần khi bạn học.",
        },
        detailSearch: {
          title: "Tìm từ",
          content: "Tìm bất kỳ từ, định nghĩa hay ví dụ nào trong bộ sưu tập này. Kết quả cập nhật khi bạn gõ — một cách nhanh để tìm từ cụ thể.",
        },
        detailFilterPills: {
          title: "Lọc theo trạng thái ôn tập",
          content: "Chia từ theo Tất cả, Chưa ôn, Đã ôn hoặc Đã ghi nhớ. Mỗi viên hiển thị số lượng của nhóm đó — dùng chúng để tập trung vào phần cần chú ý.",
        },
      },
      vocabularyPractice: {
        practiceModeTabs: {
          title: "Chọn chế độ luyện tập",
          content: "Chuyển qua lại giữa Thẻ ghi nhớ, Viết, Nghe và Ghép chữ. Chế độ Viết kết hợp gõ đáp án và điền vào chỗ trống — mỗi chế độ rèn một kỹ năng khác nhau, hãy thử tất cả để hiểu sâu hơn.",
        },
        practiceToolbar: {
          title: "Bộ điều khiển luyện tập",
          content: "Đảo chiều thẻ, trộn thứ tự từ và phát âm thanh. Dùng hộp phím tắt để học các phím tắt bàn phím giúp luyện tập nhanh hơn.",
        },
        practiceCard: {
          title: "Học thẻ",
          content: "Đọc từ hoặc định nghĩa. Nhấn chuột hoặc phím Space để lật thẻ và xem mặt còn lại. Cứ từ từ — chính xác quan trọng hơn tốc độ.",
        },
        practiceReview: {
          title: "Đánh giá khả năng nhớ",
          content: "Sau khi lật thẻ, chọn một trong bốn mức độ: Chưa nhớ (quên hẳn), Khó (vất vả), Tốt (nhớ được nhưng phải cố), hoặc Dễ (nhớ ngay). Điều này giúp thuật toán lặp lại ngắt quãng hẹn lần ôn tiếp theo đúng lúc.",
        },
        practiceSidebar: {
          title: "Theo dõi tiến độ",
          content: "Thanh bên liệt kê mọi từ trong phiên luyện tập của bạn. Từ đang được đánh dấu là từ hiện tại; những từ có dấu tick là đã ôn rồi. Nhấn vào một từ bất kỳ để nhảy tới.",
        },
      },
      studyGroups: {
        createGroup: {
          title: "Tạo nhóm của riêng bạn",
          content: "Nhấn 'Tạo nhóm' để lập một nhóm học. Đặt tên, mô tả và quyền hiển thị — rồi mời bạn bè hoặc để công khai cho mọi người tham gia.",
        },
        searchGroups: {
          title: "Tìm một nhóm",
          content: "Tìm nhóm theo tên hoặc mô tả. Kết quả cập nhật ngay khi bạn gõ.",
        },
        tabMy: {
          title: "Nhóm của tôi",
          content: "Các nhóm của bạn xuất hiện ở đây — những nhóm bạn đã tham gia hoặc tự tạo. Nhấn vào một thẻ để xem thành viên, chủ đề được chia sẻ và bảng xếp hạng.",
        },
        tabAll: {
          title: "Tất cả nhóm",
          content: "Duyệt mọi nhóm công khai tại đây. Kiểm tra số chỗ và quyền riêng tư trên từng thẻ, rồi tham gia ngay từ thẻ — hoặc mở một nhóm trước để xem thành viên đang học gì.",
        },
      },
      leaderboard: {
        metrics: {
          title: "Chọn bảng xếp hạng",
          content: "Bốn bảng xếp hạng cùng những người học theo các chỉ số khác nhau: XP kiếm được, thời gian luyện tập, từ vựng và chuỗi ngày. Tất cả hiển thị mọi bảng cùng lúc; chọn một bảng để xem toàn trang.",
        },
        periods: {
          title: "Đổi khoảng thời gian",
          content: "Hôm nay, tuần này, tháng này hoặc mọi lúc. Thứ hạng, bục vinh danh và vị trí của bạn đều thay đổi theo khoảng thời gian bạn chọn.",
        },
        board: {
          title: "Đọc bảng xếp hạng",
          content: "Ba người dẫn đầu đứng trên bục vinh danh. Những người phía dưới có thanh hiển thị tỉ lệ so với người dẫn đầu, và vị trí của bạn luôn được ghim ở cuối nếu bạn ở xa hơn.",
        },
      },
      videos: {
        requestVideo: {
          title: "Đề xuất một video",
          content: "Không tìm thấy thứ mình cần? Nhấn 'Yêu cầu video' để gợi ý một chủ đề. Video mới được thêm dựa trên các đề xuất của cộng đồng.",
        },
        searchVideos: {
          title: "Tìm video",
          content: "Nhập tại đây để tìm video theo tên. Kết quả cập nhật ngay khi bạn gõ.",
        },
        levelTabs: {
          title: "Lọc theo cấp độ",
          content: "Dùng các tab này để lọc video theo cấp độ chứng chỉ (A1–C2). Hãy chọn cấp độ phù hợp với năng lực hiện tại của bạn.",
        },
        videoList: {
          title: "Chọn một video",
          content: "Nhấn vào thẻ video bất kỳ để mở trang luyện tập. Bạn sẽ xem được video, rồi chuyển qua lại giữa chế độ luyện nói và luyện viết.",
        },
      },
      videoDetail: {
        videoPlayer: {
          title: "Xem video",
          content: "Xem video trước để làm quen với nội dung. Rồi chuyển chế độ bên dưới để luyện nhại theo phụ đề hoặc gõ lại những gì bạn nghe (chép lời).",
        },
        modeTabs: {
          title: "Chọn chế độ luyện tập",
          content: "Nhấn 'Nói' để ghi âm bạn nhại theo từng phụ đề, hoặc 'Chép lời' để gõ lại những gì bạn nghe được. Các con số cho biết bạn đã luyện bao nhiêu phụ đề ở mỗi chế độ.",
        },
        captionList: {
          title: "Danh sách phụ đề",
          content: "Toàn bộ phụ đề xuất hiện ở đây. Nhấn vào phụ đề bất kỳ để tua video đến đúng mốc thời gian đó. Ở chế độ nói, bạn có thể chuyển giữa chế độ che chữ và toàn văn.",
        },
      },
      myTopicsTime: {
        timeStats: {
          title: "Phân tích học tập của bạn",
          content: "Khu vực này cho biết tổng thời gian luyện tập, số chủ đề, trung bình mỗi chủ đề và tỉ trọng theo chuyên mục. Dùng các nút sắp xếp để sắp lại danh sách chủ đề theo Thời gian nhiều nhất, Gần đây hoặc A-Z.",
        },
        sortOptions: {
          title: "Sắp xếp chủ đề của bạn",
          content: "Nhấn 'Thời gian nhiều nhất', 'Gần đây' hoặc 'A-Z' để sắp lại danh sách chủ đề. Dùng cách này để biết nên tập trung luyện tập vào đâu tiếp theo.",
        },
      },
      studyGroupDetail: {
        tabTopics: {
          title: "Chủ đề được chia sẻ",
          content: "Thành viên chia sẻ chủ đề luyện tập tại đây. Mỗi thẻ cho biết có bao nhiêu câu hỏi và bao nhiêu từ, để bạn đánh giá được độ lớn của chủ đề trước khi mở. Chủ đề trọng tâm của nhóm hôm nay được đánh dấu.",
        },
        shareButton: {
          title: "Chia sẻ với nhóm",
          content: "Chia sẻ là thao tác chính của nhóm này. Nhấn Chia sẻ chủ đề để thêm một nội dung bạn đã dựng sẵn — nhãn nút đi theo tab bạn đang ở, nên hãy chuyển sang tab Bộ sưu tập nếu muốn chia sẻ một danh sách từ.",
        },
        tabCollections: {
          title: "Bộ sưu tập từ vựng",
          content: "Các bộ sưu tập từ vựng được chia sẻ với nhóm. Mỗi thẻ mở đầu bằng số từ, và bộ sưu tập trong ngày là bộ nhóm thống nhất để ôn tập. Dùng ông tìm kiếm và bộ lọc chuyên mục phía trên lưới để thu hẹp danh sách.",
        },
        planButton: {
          title: "Lập kế hoạch luyện tập",
          content: "Lên kế hoạch xem bạn sẽ luyện chủ đề nào vào ngày nào. Thành viên có thể thấy ai đang luyện gì và khi nào, nhờ đó dễ hẹn nhau một buổi học chung.",
        },
      },
      sharedSession: {
        sessionTopic: {
          title: "Chi tiết chủ đề",
          content: "Đây là chủ đề cùng câu hỏi và từ vựng của nó. Cuộn xuống để xem mọi thứ bạn có thể luyện tập.",
        },
        sessionStats: {
          title: "Thống kê phiên",
          content: "Xem có bao nhiêu người đã xem chủ đề này, tham gia bình luận, và theo dõi thời gian học của bạn tại đây.",
        },
        sessionLike: {
          title: "Thích chủ đề này",
          content: "Thể hiện sự trân trọng cho những chủ đề bạn thích. Lượt thích giúp người khác khám phá nội dung chất lượng.",
        },
        sessionBookmark: {
          title: "Lưu để xem sau",
          content: "Lưu chủ đề này vào danh sách đánh dấu để dễ tìm lại. Truy cập các chủ đề đã lưu từ trang chủ.",
        },
        sessionShare: {
          title: "Chia sẻ với người khác",
          content: "Chia sẻ chủ đề này với bạn bè hoặc lên mạng xã hội. Bất kỳ ai có đường dẫn đều có thể luyện tập và học từ đó.",
        },
        sessionEdit: {
          title: "Sửa chủ đề",
          content: "Chỉ bạn mới thấy nút này. Nhấn để sửa tiêu đề chủ đề, câu hỏi, từ vựng, hoặc cập nhật hướng dẫn nói.",
        },
        sessionProgress: {
          title: "Tiến độ của bạn",
          content: "Theo dõi số câu hỏi bạn đã luyện tập và số từ vựng bạn đã ôn trong quá trình học chủ đề.",
        },
        sessionPracticeAi: {
          title: "Luyện tập với AI",
          content: "Trò chuyện về chủ đề này với một gia sư AI. Bạn sẽ nhận phản hồi theo thời gian thực về độ trôi chảy, cách phát âm và cách dùng từ vựng.",
        },
        sessionPracticeVocab: {
          title: "Luyện từ vựng",
          content: "Ôn và ghi nhớ toàn bộ từ vựng của chủ đề này ở chế độ thẻ ghi nhớ và trắc nghiệm. Nắm vững các từ trước khi đi tiếp.",
        },
        sessionQuestions: {
          title: "Câu hỏi",
          content: "Duyệt toàn bộ câu hỏi luyện tập của chủ đề này. Dùng bộ lọc độ khó để tập trung vào câu hỏi Sơ cấp, Trung cấp hoặc Nâng cao. Nhấn vào một thẻ để xem toàn văn câu hỏi, đáp án và ví dụ trả lời.",
        },
        sessionQuestionNote: {
          title: "Ghi chú cho câu hỏi",
          content: "Nhấn biểu tượng ghi chú để thêm một ghi chú riêng cho bất kỳ câu hỏi nào. Ghi chú giúp bạn nhớ bối cảnh, điểm khó, hoặc liên hệ cá nhân với nội dung.",
        },
        sessionVocab: {
          title: "Từ vựng",
          content: "Mỗi từ hiển thị sẵn cách phát âm, định nghĩa và ví dụ — không cần mở. Lọc theo cấp độ CEFR bằng các viên phía trên.",
        },
        sessionVocabNote: {
          title: "Thêm ghi chú",
          content: "Nhấn biểu tượng ghi chú để thêm một ghi chú riêng cho bất kỳ từ nào. Ghi chú được lưu vào tài khoản của bạn và giúp bạn nhớ bối cảnh, cách dùng hay các chi tiết khó.",
        },
        sessionVocabSave: {
          title: "Lưu từ vựng",
          content: "Nhấn biểu tượng đánh dấu để lưu một từ vào thư viện từ vựng cá nhân của bạn. Từ đã lưu sẽ xuất hiện trong mục từ vựng để ôn tập tập trung.",
        },
        sessionGuide: {
          title: "Ghi chú của giáo viên",
          content: "Chạm để mở rộng Hướng dẫn nói — một tài liệu tra cứu nhanh với cách diễn đạt tự nhiên, cụm mở đầu câu, từ nối câu và mẹo ngữ pháp. Dùng chúng để nói trôi chảy hơn khi luyện tập.",
        },
      },
      notifications: {
        bell: {
          title: "Thông báo",
          content: "Nhắc ôn tập, hoạt động của nhóm và các cập nhật khác sẽ hiện ở đây. Hãy để ý biểu tượng chuông ở thanh trên cùng.",
        },
      },
      mySpeaking: {
        pageHero: {
          title: "Nói của tôi",
          content: "Mọi câu trả lời bạn đã ghi âm trên các chủ đề đều được gom ở đây. Xem lại bất cứ lúc nào để nghe chính mình và đọc phản hồi của AI.",
        },
        topicCard: {
          title: "Các chủ đề bạn đã ghi âm",
          content: "Mỗi thẻ hiển thị chủ đề cùng huy hiệu điểm nói trung bình của bạn. Nhấn vào một thẻ để mở ra và nghe lại mọi câu trả lời đã ghi kèm phản hồi của AI.",
        },
        recordings: {
          title: "Nghe lại và ôn tập",
          content: "Mở một chủ đề để xem từng câu hỏi, phát lại bản ghi của bạn và xem phản hồi chi tiết của AI. Dùng 'Luyện lại' để ghi một lượt mới và cải thiện điểm số.",
        },
      },
      streaks: {
        rewards: {
          title: "Chuỗi ngày & phần thưởng",
          content: "Giữ chuỗi ngày của bạn và tích XP để thăng cấp. Mở menu avatar (góc trên bên phải) bất cứ lúc nào để nhận phần thưởng và xem số liệu của bạn.",
        },
      },
    },
  },
};
